
import React, { useState, useRef, useEffect, useCallback, useReducer } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowDropDown } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  IconButton,
  MenuItem,
  Button,
  Select,
  Autocomplete,
  TextField,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Tooltip
} from '@mui/material';
import AlertNotification from '../components/AlertNotification';
import ToggleSplitPane from '../components/ToggleSplitPane';
import CaseInformationPane from '../components/CaseInformation';
import PageLoader from '../components/PageLoader';
import {
  TextAreaWithCount,
  TextFieldWithIconButton,
  UtilityLibrary,
  ApiManager,
  ScrapeDataManager,
  DebounceForm,
  ValidateRequiredFieldsInForm,
  ValidateForm,
  sessionService,
  SessionManager,
  ConfirmationDialog,
  NotificationDialog,
  CustomCheckbox,
  EnumUtility,
  ObjectUtility,
  batchStatusModel,
  NotificationDialogModel,
  CaseDocumentsModel,
  NarrativeModel,
  DeterminePermissions,
  LoggerUtility,
  TimerUtility,
  CaseWorkflowService,
  SharedService
} from '../components/ComponentsLibrary';

import { useNavigationContext } from '../functions/NavigationContext';
import { ConfigProvider, useConfigContext } from '../functions/ConfigContext';
import { useAuthContext } from '../functions/AuthContext';
import { ClientType } from '../enums/ClientType';

import * as signalR from '@microsoft/signalr';


const FormFieldEnableType = {
  PageLoad: { ID: 100, name: "PageLoad" },
  NewDocumentClick: { ID: 200, name: "NewDocumentClick" },
  BatchSelect: { ID: 300, name: "BatchSelect" },
  BatchLockedSelect: { ID: 350, name: "BatchLockedSelect" },
  CaseNumberValid: { ID: 400, name: "CaseNumberValid" },
  CaseNumberInValid: { ID: 450, name: "CaseNumberInValid" },
  NoCaseSelect: { ID: 500, name: "NoCaseSelect" },
  SaveNewDoc: { ID: 600, name: "SaveNewDoc" },
  SaveAnother: { ID: 700, name: "SaveAnother" },
  PreviousCaseSelect: { ID: 800, name: "PreviousCaseSelect" },
  PreviousCaseDeSelect: { ID: 900, name: "PreviousCaseDeSelect" }
};

const FormActionType = {
  Save: { ID: 100, name: "Save" },
  SaveAnother: { ID: 200, name: "SaveAnother" }
};

const CaseSearchSource = {
  CaseNumberField: { ID: 100, name: "CaseNumberField" },
  PreviousCaseField: { ID: 200, name: "PreviousCaseField" },
};

// Initial state for form fields
const initialState = {
  hasBatchLocked: false,
  batchSelect: true,
  ibRefreshBatch: true,
  caseNumberTxt: false,
  noCaseChk: false,
  prevCaseChk: false,
  docGroupSelect: false,
  docTypeSelect: false,
  noteForActionTxt: false,
  narrativeForStateTxt: false,
  saveBtn: false,
  saveAnotherBtn: false,
  skipDocumentBtn: false
};

// Reducer to manage enable/disable state
function reducer(state, action) {
  switch (action.type) {
    case "SET_FIELD_STATE":
      return { ...state, [action.payload.field]: action.payload.isEnabled };
    case 'SET_BUTTON_VISIBILITY':
      return { ...state, [action.payload.field]: action.payload.isVisible };
    case "SET_STATE_VARIABLE":
      return {
        ...state,
        hasBatchLocked: action.payload,
      };

    default:
      throw new Error("Unknown action type");
  }
};

function isPrevCaseInfo(config) {
  if (config) {
    if (config.hasPrevCase) {
      return true;
    }
    else {
      return false;
    }
  }
  else {
    return false;
  }
};

function checkBatchLock(config) {
  if (config) {
    if (config.hasBatchLocked) {
      return true;
    }
    else {
      return false;
    }
  }
  else {
    return false;
  }
};

// Function to apply Form Fields Enable logic
function formFieldsEnableLogic(dispatch, logicType, config = {}) {

  switch (logicType) {
    case FormFieldEnableType.PageLoad.ID:
    case FormFieldEnableType.NewDocumentClick.ID:
      if (checkBatchLock(config)) {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "batchSelect", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "ibRefreshBatch", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: true } });
      }
      else {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "batchSelect", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "ibRefreshBatch", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: false } });
      }

      dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;
    case FormFieldEnableType.BatchSelect.ID:
      //dispatch({ type: "SET_FIELD_STATE", payload: { field: "batchSelect", isEnabled: true } });
      //dispatch({ type: "SET_FIELD_STATE", payload: { field: "ibRefreshBatch", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;
    case FormFieldEnableType.BatchLockedSelect.ID:
      //dispatch({ type: "SET_FIELD_STATE", payload: { field: "batchSelect", isEnabled: true } });
      //dispatch({ type: "SET_FIELD_STATE", payload: { field: "ibRefreshBatch", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;
    case FormFieldEnableType.CaseNumberValid.ID:
      if (isPrevCaseInfo(config)) {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: true } });
      }
      else {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: false } });
      }
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;
    case FormFieldEnableType.CaseNumberInValid.ID:
      if (isPrevCaseInfo(config)) {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: true } });
      }
      else {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: false } });
      }
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;
    case FormFieldEnableType.NoCaseSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;
    case FormFieldEnableType.PreviousCaseSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;
    case FormFieldEnableType.PreviousCaseDeSelect.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;
    case FormFieldEnableType.SaveNewDoc.ID:
      if (checkBatchLock(config)) {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "batchSelect", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "ibRefreshBatch", isEnabled: false } });
      }
      else {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "batchSelect", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "ibRefreshBatch", isEnabled: true } });
      }
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: true } });
      if (isPrevCaseInfo(config)) {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: true } });
      }
      else {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: false } });
      }
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: false } });
      break;
    case FormFieldEnableType.SaveAnother.ID:
      if (checkBatchLock(config)) {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "batchSelect", isEnabled: false } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "ibRefreshBatch", isEnabled: false } });
      }
      else {
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "batchSelect", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "ibRefreshBatch", isEnabled: true } });
      }
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "caseNumberTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noCaseChk", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "prevCaseChk", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docGroupSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "docTypeSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveAnotherBtn", isEnabled: false } });
      dispatch({ type: "SET_BUTTON_VISIBILITY", payload: { field: "skipDocumentBtn", isVisible: true } });
      break;
    default:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "batchSelect", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "ibRefreshBatch", isEnabled: true } });
      break;
  }
};



// Helper function to update form values
function updateArrayStateValues(arrayStateSetter, updatedValues) {
  arrayStateSetter((prevValues) => ({
    ...prevValues,
    ...updatedValues
  }));
};



function getParentIdByChildId(data, parentFieldName, childField, childId) {
  try {
    const parent = data.find((parent) =>
      parent[childField] === childId)
      ;
    return parent ? parent[parentFieldName] : 0; // Return 0 if no parent is found
  }
  catch (error) {
    throw LoggerUtility.ProcessErrorData(error, "getParentIdByChildId", {});
  }
};

function getParentIdByChildId2(data, parentFieldName, childFieldName, childField, childId) {
  try {
    const parent = data.find((parent) =>
      parent[childFieldName].some((child) => child[childField] === childId)
    );
    return parent ? parent[parentFieldName] : 0; // Return 0 if no parent is found
  }
  catch (error) {
    throw LoggerUtility.ProcessErrorData(error, "getParentIdByChildId", {});
  }
};


const NewDocument = React.memo(({ }) => {
  useEffect(() => {
    document.title = 'YTrac :: New Document';

  }, []); // Empty dependency array to set the title only on mount

  const location = useLocation();
  const navigate = useNavigate();
  const { configLibrary } = useConfigContext();
  const apiMainFrameBaseUrl = configLibrary.apiMainframeUrl;
  
  /* 
      // Extract query string value
      const query = new URLSearchParams(location.search);
      const queryParam = query.get('idxLock'); // e.g., ?myParam=value
  
    useEffect(() => {
      let indexToAppendToUrl = UtilityLibrary.getRandomFourDigitNumber();
      const basePath = location.pathname;
      const trapUrl = `${basePath}?idxLock=${indexToAppendToUrl}`;    
  
      // Push two entries to trap back/forward
      window.history.replaceState({ page: basePath }, '', basePath);
      window.history.pushState({ page: trapUrl }, '', trapUrl);
  
      /* window.history.replaceState({ page: 'Some Dummy' }, '', basePath);
      window.history.pushState({ page: 'Some Dummy' }, '', trapUrl);
      window.history.pushState({ page: 'Some Dummy' }, '', trapUrl); 
    }, [queryParam]); */

  const { setActivePage, setIsFormContentChanged, setDialogContent } = useNavigationContext();

  const leftPaneTitle = "Documents";
  const rightPaneTitle = "Document Viewer";

  const errorFormName = "New Document";

  // Create a ref for the child component
  const caseInfoRef = useRef(null);

  // Ref to hold SignalR connection instance
  const connectionRef = useRef(null);

  // Ref to hold the timeout ID for the *currently active* request
  const timeoutIdRef = useRef(null);


  const [signalrRequestId, setSignalrRequestId] = useState(null);
  const [signalrFinalResult, setSignalrFinalResult] = useState(null);

  const masterServerDBSchema = SessionManager.GetDataFromSession("SiteSettingsDetailsKey")?.masterDBSchema;
  const stateID = SessionManager.GetDataFromSession("LoginUserInfoKey")?.stateID;

  const initialFormValues = {
    batchSelect: "",
    caseNumberTxt: "",
    yTracCaseID: 0,
    caseNumberSearched: "",
    noCaseChk: false,
    prevCaseChk: false,
    docGroupSelect: { id: 0, name: "All" },
    //docGroupSelect: "",
    docTypeSelect: { id: -123456, name: "" },
    //docTypeSelect: "",
    noteForActionTxt: "",
    narrativeForStateTxt: ""
  };

  const clearSaveFormValues = {
    caseNumberTxt: "",
    yTracCaseID: 0,
    caseNumberSearched: "",
    noCaseChk: false,
    prevCaseChk: false,
    docGroupSelect: { id: 0, name: "All" },
    //docGroupSelect: "",
    docTypeSelect: { id: -123456, name: "" },
    //docTypeSelect: "",
    noteForActionTxt: "",
    narrativeForStateTxt: ""
  };

  const clearSaveAnotherFormValues = {
    caseNumberTxt: "",
    yTracCaseID: 0,
    caseNumberSearched: "",
    noCaseChk: false,
    prevCaseChk: false
  };

  const [requiredFieldLabels, setRequiredFieldLabels] = useState({
    batchSelect: "",
    caseNumberTxt: "",
    noCaseChk: "",
    prevCaseChk: "",
    docGroupSelect: "",
    docTypeSelect: "",
    noteForActionTxt: "",
    narrativeForStateTxt: "",
    caseSelectionRequired: ""
  });

  const [userInfo, setUserInfo] = useState(null);
  const [userId, setUserId] = useState(-123456);
  const userIdRef = useRef(-123456);

  const [formState, dispatch] = useReducer(reducer, initialState);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [requiredFormValueChanged, setRequiredFormValueChanged] = useState(false);

  const initialCaseNumberIconsDisabled = { search: false, copy: false, clear: false };
  const [caseNumberIconsDisabled, setCaseNumberIconsDisabled] = useState(initialCaseNumberIconsDisabled);
  const [disableKeyDownOnCaseSearch, setDisableKeyDownOnCaseSearch] = useState(false);

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [isSaveAnotherEnabled, setIsSaveAnotherEnabled] = useState(false);
  const [isSaveAnotherTriggered, setIsSaveAnotherTriggered] = useState(false);
  const [caseSaveAnotherArray, setCaseSaveAnotherArray] = useState([]);
  const [caseValidationType, setCaseValidationType] = useState(EnumUtility.CaseType.Case);

  const [isEditable, setIsEditable] = useState(false);
  const [isConfidential, setIsConfidential] = useState(false);

  //Batch dropdown API code 
  const [batchlists, setBatchlists] = useState([]);

  const [externalCaseID, setExtCaseID] = useState(0);
  const [extCaseSearch, setExtCaseSearch] = useState(false);
  const [showExtDocGrid, setShowExtDocGrid] = useState(false);

  const caseNumberRef = useRef();
  const caseUniqueID = "caseNumber";
  const initialPrevCase = {
    hasPrevCase: false,
    prevCaseNumber: ""
  }
  const [prevCaseInfo, setPrevCaseInfo] = useState(initialPrevCase);

  // Validation patterns
  const siteSettingDetail = JSON.parse(SessionManager.GetValueFromSession(SessionManager.SiteSettingsDetailsKey));
  const caseNumberRegex = siteSettingDetail.CaseNumberRegex || { caseRegexValue: "", caseRegexMessage: "" };
  //const caseNumberRegex = JSON.parse(SessionManager.GetValueFromSession(SessionManager.CaseNumberRegexKey)) || { caseRegexValue: "", caseRegexMessage: "" };

  // Narrative Character Limit
  const narrativeCharLimit = siteSettingDetail.NarrativeCharsLimit || 500;
  //const narrativeCharLimit = SessionManager.GetValueFromSession(SessionManager.NarrativeCharsLimitKey) || 500;

  // Notes Character Limit
  const notesCharLimit = siteSettingDetail.NotesCharsLimit || 500;
  //const notesCharLimit = SessionManager.GetValueFromSession(SessionManager.NotesCharsLimitKey) || 500;

  // Soft Lock Timer - set length of time in minutes that soft lock should last
  const softLockTimer = parseInt(siteSettingDetail.SoftLockTimer, 10) || 30;

  const [lockedBatchInfo, setLockedBatchInfo] = useState({
    hasLockedBatch: false,
    lockedBatchID: 0
  });
  const [externalBatchID, setExtBatchID] = useState(0);

  // Used to release soft lock on previously viewed batch
  const [previousLockInfo, setpreviousLockInfo] = useState({});
  const previousLockInfoRef = useRef(previousLockInfo);

  // State to track when focus should be set to case search field after notification dialog closes
  const [shouldFocusCaseSearch, setShouldFocusCaseSearch] = useState(false);

  const [leftPaneMessage, setLeftPaneMessage] = useState("");

  const [isPageLoading, setIsPageLoading] = useState(false);

  const noteForActionRef = useRef();
  const narrativeForStateRef = useRef();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationDialogContent, setConfirmationDialogContent] = useState([]);

  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationDialogContent, setNotificationDialogContent] = useState([]);
  const [timerNotificationDialogClose, setTimerNotificationDialogClose] = useState(5000);
  const [timerAutoCloseNotificationDialog, setTimerAutoCloseNotificationDialog] = useState(false);

  // State for Index success message
  const [indexSuccessMessageVisible, setIndexSuccessMessageVisible] = useState(false);
  const [customSuccessMessage, setCustomSuccessMessage] = useState("");

  // Initial state for posting the fields to SAVE THE DOCUMENT
  const initialNewDocData = {
    userLoginID: 0,
    fKM_UserLoginID: 0,
    orgID: 0,
    batchStatusID: 0,
    batchID: 0,
    batchName: "",
    batchDocumentID: 0,
    docGroupID: 0,
    documentTypeID: 0,
    documentStatusID: 0,
    documentName: "",
    documentLocation: "",
    notes: "",
    narrative: "",
    yTracCaseID: 0,
    caseNumber: "",
    nocase: 0,
    indexingCompleteTS: null,
    docQueueID: 0,
    "FKM_ScanUserLoginID": null,
    indexTS: null,
    "FKM_IndexUserLoginID": null,
    "FKM_LastIndexUserLoginID": null,
    lastIndexTS: null,
    documentOrder: 0,
    yTracSiteID: 0,
    documentUNCPath: "",
    masterServerDBSchema,
    actionStartTime: null
  };

  // Array for posting the fields to SAVE THE DOCUMENT 
  const [postNewDocData, setPostNewDocData] = useState(initialNewDocData);

  const clearPostNewDocData = () => {
    try {
      const updatedDocValues = {
        ...postNewDocData,
        ...initialNewDocData
      };
      updateArrayStateValues(setPostNewDocData, updatedDocValues);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearPostNewDocData", {});
    }
  }

  const batchDocumentProcess = EnumUtility.BatchDocumentProcess;

  const defaultPermissionsForFields = {
    newDocumentBtn: { read: false, write: true, hide: false },
    batchSelect: { read: false, write: true, hide: false },
    caseNumberTxt: { read: false, write: true, hide: false },
    noCaseChk: { read: false, write: true, hide: false },
    prevCaseChk: { read: false, write: true, hide: false },
    docGroupSelect: { read: false, write: true, hide: false },
    docTypeSelect: { read: false, write: true, hide: false },
    noteForActionTxt: { read: false, write: true, hide: false },
    narrativeForStateTxt: { read: false, write: true, hide: false },
    cancelBtn: { read: false, write: true, hide: false },
    leftPaneMessageLbl: { read: false, write: true, hide: false },
    saveBtn: { read: false, write: true, hide: false },
    saveAnotherBtn: { read: false, write: true, hide: false },
    skipDocumentBtn: { read: false, write: true, hide: false },
  };
  const [formFieldAccess, setFormFieldAccess] = useState(defaultPermissionsForFields);

  //Document Group dropdown API code
  //const [docgrouplists, setdocgrouplists] = useState({ documentGroupTypes: [] });
  const [docgrouplists, setDocgrouplists] = useState([]); // To load Document Type dropdown based on Group
  const [docGroups, setDocGroups] = useState([]); // For Document Group dropdown
  const [docGroupsWithCase, setDocGroupsWithCase] = useState([]); // For Document Group dropdown When "NO CASE" is not selected
  const [docGroupsWithNoCase, setDocGroupsWithNoCase] = useState([]); // For Document Group dropdown When "NO CASE" is selected

  //Document Type dropdown API code
  const [documentTypeList, setDocumentTypeList] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const documentTypeRef = useRef(null);

  const [timeActionStart, setTimeActionStart] = useState(new Date().toISOString());
  const [timerView, setTimerView] = useState("00:00");
  const timerRef = React.useRef(null); // Keep the TimerUtils instance
  const isTimerVisible = false;

  // Initialize timer only once
  if (!timerRef.current) {
    timerRef.current = new TimerUtility(setTimerView);
  }; 

  function clearFormFields(formActionType) {
    try {
      let validationType = EnumUtility.CaseType.Case;

      switch (formActionType) {
        case FormActionType.Save.ID:
          const updatedFormValues = { ...formValues, ...clearSaveFormValues };
          updateArrayStateValues(setFormValues, updatedFormValues);
          noteForActionRef.current?.clear();
          narrativeForStateRef.current?.clear();
          setCaseValidationType(validationType);
          debouncedValidation(updatedFormValues, validationType, isConfidential);
          break;

        case FormActionType.SaveAnother.ID:
          const updatedFormValues2 = { ...formValues, ...clearSaveAnotherFormValues };
          updateArrayStateValues(setFormValues, updatedFormValues2);
          setCaseValidationType(validationType);
          debouncedValidation(updatedFormValues2, validationType, isConfidential);
          break;

        default:
          setCaseValidationType(validationType);
          debouncedValidation(initialFormValues, validationType, isConfidential);
          break;
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearFormFields", {});
    }

  };

  function getValidationRules(values, validationType, confidential) {
    switch (validationType) {
      case EnumUtility.CaseType.Case:
        return getCaseValidationRules(values, confidential);

      case EnumUtility.CaseType.NoCase:
        return getNoCaseValidationRules(values);

      default:
        break;
    }
  };

  const getCaseValidationRules = (values, confidential) => {
    let prevCaseRequired = false;
    if (values.caseNumberTxt === '') {
      if (prevCaseInfo.hasPrevCase) {
        prevCaseRequired = true;
      }
    }

    // Check if either case has been searched OR "No Case" is checked
    const hasCaseOrNoCaseSelected = values.noCaseChk || (values.caseNumberSearched !== "");

    let caseNumberValidation = { required: false };
    if (siteSettingDetail.CaseNumberInsertAllow === "1") {
      caseNumberValidation = { required: true, validator: () => values.caseNumberSearched !== "", requireValueChange: true, };
    }
    else {
      caseNumberValidation = { required: true, validator: () => values.yTracCaseID > 0, requireValueChange: true, };
    }
    let noteValidation = {};
    // if (isConfidential || confidential) {
    //   noteValidation = { required: true, validator: (value) => value !== "", requireValueChange: true, };
    // }
    // else {
    //   noteValidation = { required: false, requireValueChange: false, };
    // }
    noteValidation = { required: false, requireValueChange: false, };

    return {
      batchSelect: { required: true, validator: (value) => value !== "", requireValueChange: false, },
      caseNumberTxt: caseNumberValidation,
      noCaseChk: { required: values.caseNumberTxt === '' ? true : false, requireValueChange: false, },
      prevCaseChk: { required: prevCaseRequired, requireValueChange: false, },
      docGroupSelect: { required: true, validator: (value) => value.id >= 0, valueChangeCheck: (value) => value.id > 0, requireValueChange: true, },
      docTypeSelect: { required: true, validator: (value) => value.id > 0, requireValueChange: true, },
      //docGroupSelect: { required: true, validator: (value) => value !== "" },
      //docTypeSelect: { required: true, validator: (value) => value !== "" },
      noteForActionTxt: noteValidation,
      narrativeForStateTxt: { required: true, validator: (value) => value !== "", requireValueChange: true, },
      // Add a special validation rule to ensure case search or no case is selected
      caseSelectionRequired: {
        required: true,
        validator: () => hasCaseOrNoCaseSelected,
        requireValueChange: false,
      }
    };
  };

  const getNoCaseValidationRules = (values) => {
    // For no case validation, we already have noCaseChk = true, so case selection is satisfied
    const hasCaseOrNoCaseSelected = values.noCaseChk || (values.caseNumberSearched !== "");

    return {
      batchSelect: { required: true, validator: (value) => value !== "", requireValueChange: false, },
      caseNumberTxt: { required: false, requireValueChange: false, },
      noCaseChk: { required: true, validator: (value) => value === true, requireValueChange: false, },
      prevCaseChk: { required: false, requireValueChange: false, },
      docGroupSelect: { required: true, validator: (value) => value.id >= 0, valueChangeCheck: (value) => value.id > 0, requireValueChange: true, },
      docTypeSelect: { required: true, validator: (value) => value.id > 0, requireValueChange: true, },
      noteForActionTxt: { required: true, validator: (value) => value !== "", requireValueChange: true, },
      narrativeForStateTxt: { required: false, requireValueChange: false, },
      // Add the same case selection validation rule
      caseSelectionRequired: {
        required: true,
        validator: () => hasCaseOrNoCaseSelected,
        requireValueChange: false,
      }
    };
  };

  const getDefaultValidationRules = (values) => {
    const hasCaseOrNoCaseSelected = values.noCaseChk || (values.caseNumberSearched !== "");

    return {
      batchSelect: { required: true, validator: (value) => value !== "" },
      caseNumberTxt: { required: true, validator: () => values.yTracCaseID > 0 },
      noCaseChk: { required: true, validator: (value) => value === true },
      prevCaseChk: { required: true, validator: (value) => value === true },
      docGroupSelect: { required: true, validator: (value) => value !== "" },
      docTypeSelect: { required: true, validator: (value) => value !== "" },
      noteForActionTxt: { required: true, validator: (value) => value !== "" },
      narrativeForStateTxt: { required: true, validator: (value) => value !== "" },
      // Add the same case selection validation rule
      caseSelectionRequired: {
        required: true,
        validator: () => hasCaseOrNoCaseSelected,
        requireValueChange: false,
      }
    };
  };

  // Custom function to calculate which fields should show asterisks
  const calculateRequiredAsterisks = (values, confidential = false) => {
    const asterisks = {
      batchSelect: "",
      caseNumberTxt: "",
      noCaseChk: "",
      prevCaseChk: "",
      docGroupSelect: "",
      docTypeSelect: "",
      noteForActionTxt: "",
      narrativeForStateTxt: "",
      caseSelectionRequired: ""
    };

    // Only show asterisks AFTER case search has been performed OR No Case is selected
    const hasCaseSearched = values.caseNumberSearched && values.caseNumberSearched.trim() !== "";
    const hasNoCaseSelected = values.noCaseChk;
    const shouldShowAsterisks = hasCaseSearched || hasNoCaseSelected;

    if (!shouldShowAsterisks) {
      // No asterisks until case search or No Case is selected
      return asterisks;
    }

    // Doc Type is always required and should show asterisk if not selected (after case action)
    const isDocTypeSelected = values.docTypeSelect && values.docTypeSelect.id > 0;
    asterisks.docTypeSelect = isDocTypeSelected ? "" : "*";

    if (values.noCaseChk) {
      // No Case mode: Doc Type and Note are required
      const isNoteFilledOut = values.noteForActionTxt && values.noteForActionTxt.trim() !== "";
      asterisks.noteForActionTxt = isNoteFilledOut ? "" : "*";
      asterisks.narrativeForStateTxt = ""; // Not required in No Case mode
    } else {
      // Case Search mode: Doc Type and Narrative are required  
      const isNarrativeFilledOut = values.narrativeForStateTxt && values.narrativeForStateTxt.trim() !== "";
      asterisks.narrativeForStateTxt = isNarrativeFilledOut ? "" : "*";
      asterisks.noteForActionTxt = ""; // Not required in Case Search mode when case not confidential
      const isNoteForActionFilledOut = values.noteForActionTxt && values.noteForActionTxt.trim() !== "";
      // If confidential and note not filled then  make ytrac note required
      // if ((confidential || isConfidential) && !isNoteForActionFilledOut) {
      //   asterisks.noteForActionTxt = "*";
      // }
    }
    return asterisks;
  };

  // Debounced validation using the centralized validateForm
  const debouncedValidation = useCallback(
    DebounceForm((values, validationType, confidential = false) => {
      try {
        const rules = getValidationRules(values, validationType, confidential);

        // Did this because noteForActionTxt in formState is false even when case number is valid
        const formStateConfidential = { ...formState, noteForActionTxt: confidential }
        const validationResults = ValidateRequiredFieldsInForm(values, rules, formStateConfidential);

        // // Use custom asterisk calculation instead of validation results
        const customAsterisks = calculateRequiredAsterisks(values, confidential);
        setRequiredFieldLabels(customAsterisks);

        const isValid = validationResults.isFormValid;

        // Custom validation logic that matches asterisk requirements
        const isCustomValidationPassed = (values) => {
          // Only validate after case search has been performed OR No Case is selected
          const hasCaseSearched = values.caseNumberSearched && values.caseNumberSearched.trim() !== "";
          const hasNoCaseSelected = values.noCaseChk;
          const shouldValidate = hasCaseSearched || hasNoCaseSelected;

          if (!shouldValidate) {
            // Before case action, buttons should be disabled
            return false;
          }

          // Doc Type is always required
          const isDocTypeSelected = values.docTypeSelect && values.docTypeSelect.id > 0;
          if (!isDocTypeSelected) {
            return false;
          }

          if (values.noCaseChk) {
            // No Case mode: Doc Type and Note are required
            const isNoteFilledOut = values.noteForActionTxt && values.noteForActionTxt.trim() !== "";
            return isNoteFilledOut;
          } else {
            // Case Search mode: Doc Type and Narrative are required  
            const isNarrativeFilledOut = values.narrativeForStateTxt && values.narrativeForStateTxt.trim() !== "";
            return isNarrativeFilledOut;
          }
        };

        const finalValidState = isValid && isCustomValidationPassed(values);

        // Dynamically update labels with "*" for required fields
        /* const updatedLabels = Object.keys(rules).reduce((labels, fieldName) => {
          labels[fieldName] = rules[fieldName].required
            ? "*"
            : " ";
          return labels;
        }, {});
        setRequiredFieldLabels(updatedLabels);
        const isValid = ValidateForm(values, rules); */

        setIsSaveEnabled(finalValidState);

        if (values.noCaseChk) {
          setIsSaveAnotherEnabled(false);
        }
        else {
          setIsSaveAnotherEnabled(finalValidState);
        }

        if (validationResults.requiredValueChanged) {
          setIsFormContentChanged(true);
          setDialogContent(EnumUtility.ConfirmDialogType.NewDocumentCancel);
        }
        else {
          setIsFormContentChanged(false);
        }
        setRequiredFormValueChanged(validationResults.requiredValueChanged);

      }
      catch (error) {
        throw LoggerUtility.ProcessErrorData(error, "debouncedValidation", {});
      }
    }, 300),
    []
  );



  const toUpperCase = (stringToUpperCase) => {
    return String(stringToUpperCase).toUpperCase();
  };

  const toLowerCase = (stringToLowerCase) => {
    return String(stringToLowerCase).toLowerCase();
  };


  const [alertProps, setAlertProps] = useState({
    type: '',
    message: '',
    open: false,
  });

  const showAlert = (type, message) => {
    setAlertProps({ type, message, open: true });
  };


  //=======================================================================
  // Tabs Feature - Begin
  //=======================================================================

  // Enable/Disable Batch dropdown based on the batch lock status
  /* useEffect(() => {
    const fieldEnableCondition = formState.hasBatchLocked; // Example condition
    dispatch({
      type: "SET_FIELD_STATE",
      payload: { field: "batchSelect", isEnabled: !fieldEnableCondition },
    });
  }, [formState.hasBatchLocked]); */

  const getLockedBatchIDbyUser = async (batchList, userID) => {
    try {
      let updatedFormValues = formValues;
      if (batchList && batchList.length > 0) {
        // TO-DO: Modify code - Define variable values. Remove Hard-Coded Values
        const loginUserInfo = sessionService.getUser();
        const loginUserInfoID = loginUserInfo ? loginUserInfo.userInfoID : -123456;
        const params = { userID: loginUserInfoID }; // To-do : UserInfoID
        const lockedBatchData = await ApiManager.GetUserLockedBatchFromApi(params);
        let lockedBatchID = 0;
        if (lockedBatchData.length > 0) {
          lockedBatchID = lockedBatchData[0].lockedBatchID || 0;

          // if (lockedBatchID > 0) {
          //   //await updateFieldEnableState(dispatch, "batchSelect", true, (val) => val === false);
          // }

          if (batchList.length > 0) {
            const batchID = UtilityLibrary.getIdByValueFromArray(batchList, 'batchID', lockedBatchID);
            if (batchID > 0) {
              updatedFormValues = { ...formValues, batchSelect: batchID };
              updateArrayStateValues(setFormValues, updatedFormValues);
              await loadBatchDetails(batchID);
            }
          }
        }

        const updatedLockedBatchInfo = {
          ...lockedBatchInfo,
          hasLockedBatch: lockedBatchID > 0 ? true : false,
          lockedBatchID: lockedBatchID
        };
        updateArrayStateValues(setLockedBatchInfo, updatedLockedBatchInfo);
        return { updatedFormValues: updatedFormValues, updatedLockedBatchInfo: updatedLockedBatchInfo };
      }
      else {
        handleNotificationDialog(EnumUtility.BatchDocumentProcess.NoBatchAvailableForIndex);
        return { updatedFormValues: updatedFormValues, updatedLockedBatchInfo: null };
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "getLockedBatchIDbyUser", {});
    }
  };

  // Function to handle New Document Click
  const handleNewDocumentClick = async () => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      console.log('🔍 SessionManager keys check:', {
        SiteSettingsDetailsKey: SessionManager.GetDataFromSession("SiteSettingsDetailsKey"),
        SiteSettingsDetails: SessionManager.GetDataFromSession("SiteSettingsDetails"),
        LoginUserInfo: SessionManager.GetDataFromSession("LoginUserInfo"),
        LoginUserInfoKey: SessionManager.GetDataFromSession("LoginUserInfoKey")
      });
      const freshBatches = await fetchBatches();

      setIsEditable(true);
      setIsSaveAnotherTriggered(false);
      setPrevCaseInfo(initialPrevCase);
      const { updatedFormValues, updatedLockedBatchInfo } = await getLockedBatchIDbyUser(freshBatches, 0); // TO-DO: Modify Code - Remove the hard-coded user-id value
      if (updatedLockedBatchInfo && updatedLockedBatchInfo.hasLockedBatch) {
        formFieldsEnableLogic(dispatch, FormFieldEnableType.NewDocumentClick.ID, { hasBatchLocked: updatedLockedBatchInfo.hasLockedBatch });
        debouncedValidation(updatedFormValues, caseValidationType, isConfidential);
      }
      else {
        formFieldsEnableLogic(dispatch, FormFieldEnableType.NewDocumentClick.ID, { hasBatchLocked: lockedBatchInfo.hasLockedBatch });
        debouncedValidation(updatedFormValues, caseValidationType, isConfidential);
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNewDocumentClick");
    }
    finally { setIsPageLoading(false); }
  };

  const handleReIndexClick = () => {
    setActivePage('newDocument');
    const reIndexPageProps = EnumUtility.DocumentSearchPageProperty.ReIndexDocument;

    navigate('/documentsearch', {
      state: reIndexPageProps,
    });
  };

  const clearCaseTabInfo = () => {
    if (caseInfoRef.current) {
      // Call the child method with parameters
      caseInfoRef.current.clearCaseInfoFromExternal(externalBatchID, showExtDocGrid);
    }
  };


  const onCaseSearch = async (caseNumber, caseSearchSource) => {
    try {
      if (caseInfoRef.current) {
        // Call the child method with parameters
        const caseID = toUpperCase(caseNumber);
        caseInfoRef.current.onSelectCaseFromExternal(caseID, showExtDocGrid, caseSearchSource);
      }
    }
    catch (error) {
      await LoggerUtility.ProcessErrorData(error, "onCaseSearch", {});
    }
  };

  // Function to handle search
  const handleCaseSearch = async (event) => {
    setIsPageLoading(true);
    try {
        const siteID = SessionManager.GetDataFromSession("LoginUserInfoKey")?.siteID;
        const settingsDetailIDs = [ClientType.SettingsDetail.Code_Switching__Check_Mainframe_Connectivity_On_Search];
        const checkMainframe = await ApiManager.GetSettingsDetailValuesFromApi({
          settingsDetailIDList: settingsDetailIDs.join(","),
          ytracSiteID: siteID
        });

      await simulateAsyncDelay();
      const isValidCaseFormat = checkCaseNumberRegEx(formValues.caseNumberTxt);
      if (isValidCaseFormat) {
        let validCaseSearch = true;
        if (isSaveAnotherTriggered) {
          validCaseSearch = await checkCaseNumberOnSaveAnother(formValues.caseNumberTxt);
        }
        if (!validCaseSearch) {
          // Check if case number should be preserved based on error message
          const shouldPreserve = EnumUtility.shouldPreserveCaseNumber(EnumUtility.BatchDocumentProcess.CaseNumberAddedAgain);
          await clearCaseSearchInfo(shouldPreserve);
          clearCaseTabInfo();
          handleNotificationDialog(EnumUtility.BatchDocumentProcess.CaseNumberAddedAgain);
        }
        else {
          await onCaseSearch(formValues.caseNumberTxt, CaseSearchSource.CaseNumberField.ID);
        }
      }
      else {
        const dialogContent = {
          ...EnumUtility.NotificationAlerts.CaseNumberFormatInvalid,
          dynamicMessageContent: [formValues.caseNumberTxt, caseNumberRegex.caseRegexMessage],
        };
        handleNotificationDialog(dialogContent);
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearch");
    }
    finally { setIsPageLoading(false); }
  };

  const clearCaseSearchInfo = async (preserveCaseNumber = false) => {
    try {
      const updatedDocValues = {
        ...postNewDocData,
        yTracCaseID: 0,
        caseNumber: "",
        nocase: 0,
      };
      updateArrayStateValues(setPostNewDocData, updatedDocValues);

      const updatedFormValues = {
        ...formValues,
        caseNumberTxt: preserveCaseNumber ? formValues.caseNumberTxt : "",
        yTracCaseID: 0,
        caseNumberSearched: "",
      };
      updateArrayStateValues(setFormValues, updatedFormValues);
      debouncedValidation(updatedFormValues, caseValidationType, isConfidential);
    }
    catch (error) {
      await LoggerUtility.ProcessErrorData(error, "clearCaseSearchInfo", {});
    }
  };

  // Function to handle clear of case search value
  const handleCaseClear = async () => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      await clearCaseSearchInfo();
      clearCaseTabInfo();
      caseNumberRef.current.setValidationMessage("");
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseClear");
    }
    finally { setIsPageLoading(false); }
  };

  // Function to handle case search completion
  const handleCaseSearchComplete = async (hasValidCase, caseSearchInfo, caseSearchSource) => {
    try {
      let updatedDocValues = postNewDocData;
      let updatedFormValues = formValues;

      const caseInfo = caseSearchInfo.caseData;

      if (hasValidCase) {
        const confidential = CaseWorkflowService.checkConfidential(caseInfo);
        if (confidential) {
          setIsConfidential(true);
        }
        else {
          setIsConfidential(false);
        }

        //timerRef.current.startTimer();
        setTimeActionStart(new Date().toISOString());

        updatedDocValues = {
          ...postNewDocData,
          yTracCaseID: caseInfo.yTracCaseID,
          caseNumber: caseInfo.caseNumber,
          nocase: 0,
        };

        updatedFormValues = {
          ...formValues,
          yTracCaseID: caseInfo.yTracCaseID,
          caseNumberTxt: caseInfo.caseNumber,
          caseNumberSearched: caseInfo.caseNumber,
          prevCaseChk: caseSearchSource === CaseSearchSource.PreviousCaseField.ID ? true : false,
        }

        if (isSaveAnotherTriggered) {
          formFieldsEnableLogic(dispatch, FormFieldEnableType.SaveAnother.ID);
        }
        else {
          const configPrevCase = {
            hasPrevCase: prevCaseInfo.hasPrevCase,
            prevCaseNumber: prevCaseInfo.prevCaseNumber
          }
          if (caseSearchSource === CaseSearchSource.CaseNumberField.ID) {
            formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberValid.ID, configPrevCase);
          }
          else if (caseSearchSource === CaseSearchSource.PreviousCaseField.ID) {
            formFieldsEnableLogic(dispatch, FormFieldEnableType.PreviousCaseSelect.ID, configPrevCase);
          }
        }
        debouncedValidation(updatedFormValues, caseValidationType, confidential);
        // handleNotificationDialog(EnumUtility.ConfirmDialogType.CaseNumberFound);

        updateArrayStateValues(setPostNewDocData, updatedDocValues);
        updateArrayStateValues(setFormValues, updatedFormValues);
      }
      else {
        if (siteSettingDetail.CaseNumberInsertAllow === "1") {
          setTimeActionStart(new Date().toISOString());

          updatedDocValues = {
            ...postNewDocData,
            yTracCaseID: 0,
            caseNumber: formValues.caseNumberTxt,
            nocase: 0,
          };

          updatedFormValues = {
            ...formValues,
            yTracCaseID: 0,
            caseNumberSearched: formValues.caseNumberTxt,
            prevCaseChk: caseSearchSource === CaseSearchSource.PreviousCaseField.ID ? true : false,
          }

          // Check if we're in Save+Another mode - maintain Save+Another state regardless of case validity
          if (isSaveAnotherTriggered) {
            formFieldsEnableLogic(dispatch, FormFieldEnableType.SaveAnother.ID);
          }
          else {
            const configPrevCase = {
              hasPrevCase: prevCaseInfo.hasPrevCase,
              prevCaseNumber: prevCaseInfo.prevCaseNumber
            }
            if (caseSearchSource === CaseSearchSource.CaseNumberField.ID) {
              formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberValid.ID, configPrevCase);
            }
            else if (caseSearchSource === CaseSearchSource.PreviousCaseField.ID) {
              formFieldsEnableLogic(dispatch, FormFieldEnableType.PreviousCaseSelect.ID, configPrevCase);
            }
          }

          debouncedValidation(updatedFormValues, caseValidationType, isConfidential);
          // handleNotificationDialog(EnumUtility.ConfirmDialogType.CaseNumberFound);

          updateArrayStateValues(setPostNewDocData, updatedDocValues);
          updateArrayStateValues(setFormValues, updatedFormValues);
        }
        else {
          //timerRef.current.resetTimer();
          let shouldPreserve = false;
          let errorMessage = {};
          if (caseSearchInfo.statusCode === EnumUtility.CaseSearchResultCode.CaseNotFound.ID) {
            const loginUserInfo = sessionService.getUser();
            const userStateID = loginUserInfo ? loginUserInfo.stateID : -1;
            errorMessage = EnumUtility.getCaseNumberNotFoundError(userStateID);
            shouldPreserve = true;
            // Only show error if user is in MS/KS/WY, otherwise skip showing error
            if (!errorMessage) {
              return; // Exit without showing error for other states
            }
          }
          else if ([ EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID,
            EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.ID].includes(caseSearchInfo.statusCode )) {
            let messageContentID = null;
            if (caseSearchInfo.statusCode === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID){
              messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System
            }
            else{
              messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
            }
            const messageContent = await SharedService.getMessageContent(messageContentID)
            errorMessage = { NotificationType: 'error', Message: messageContent?.messageText?.replace(/\\n/g, "\u00A0\n") ?? "" }
            shouldPreserve = true;
          }
          else if (caseSearchInfo.statusCode === EnumUtility.CaseSearchResultCode.Enter_A_Function_Error.ID) {
            let messageContentID = ClientType.MessageContent.N_UI7058_ENTER_A_FUNCTION_Error_Occured;
            const messageContent = await SharedService.getMessageContent(messageContentID)
            errorMessage = { NotificationType: 'error', Message: messageContent?.messageText?.replace(/\\n/g, "\u00A0\n") ?? "" }
            //  errorMessage = { ID: 321, Name: "ENTERAFUNCTION", NotificationType: EnumUtility.NotificationType.Warning, Title: "EAF", Message: caseSearchInfo.errorMessage };
            shouldPreserve = true;
          }
          else {
            errorMessage = { ID: 321, Name: "NetworkError", NotificationType: EnumUtility.NotificationType.Warning, Title: "Warning", Message: caseSearchInfo.errorMessage };
          }

          // // Check if case number should be preserved based on error message
          // const shouldPreserve = EnumUtility.shouldPreserveCaseNumber(errorMessage);
          
          // Only clear case info if case number should not be preserved
          if (!shouldPreserve) {
            await clearCaseSearchInfo();
          }

          formFieldsEnableLogic(dispatch, FormFieldEnableType.BatchSelect.ID);
          debouncedValidation(formValues, caseValidationType, isConfidential);
          const dialogContent = {
            ...errorMessage,
            dynamicMessageContent: [formValues.caseNumberTxt],
          };
          handleNotificationDialog(dialogContent);
        }
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearchComplete");
    }
  };

  const changeCaseNumberIconsDisable = (iconsEnabledValue) => {
    try {
      const updatedCaseNumberIconsEnabled = {
        ...caseNumberIconsDisabled,
        search: iconsEnabledValue.search,
        copy: iconsEnabledValue.copy,
        clear: iconsEnabledValue.clear,
      };
      updateArrayStateValues(setCaseNumberIconsDisabled, updatedCaseNumberIconsEnabled);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "changeCaseNumberIconsDisable", {});
    }
  };

  const checkCaseNumberRegEx = (caseNumber) => {// RC removed async
    try {
      let isValidFormat = true;
      const caseNumberRegexValue = new RegExp(caseNumberRegex.caseRegexValue || "");

      if ((caseNumberRegexValue !== "") && (caseNumber !== "")) {
        if (!caseNumberRegexValue.test(caseNumber)) {
          caseNumberRef.current.setValidationMessage(caseNumberRegex.caseRegexMessage);
          changeCaseNumberIconsDisable({ search: true, copy: false, clear: false });
          setDisableKeyDownOnCaseSearch(true);
          isValidFormat = false;
        }
        else {
          caseNumberRef.current.setValidationMessage("");
          changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
          setDisableKeyDownOnCaseSearch(false);
        }
      }
      else {
        caseNumberRef.current.setValidationMessage("");
        changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
        setDisableKeyDownOnCaseSearch(false);
      }

      return isValidFormat;
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "checkCaseNumberRegEx", {});
    }
  };

  const handleCaseChange = async (e) => {
    try {
      // RC do not make any async calls before case number 
      // state update to ensure the case number cursor stays 
      // in place while editing
      const nameText = e.target.name;
      const valueText = e.target.value;
      const loginState = SessionManager.GetValueFromSession(SessionManager.LoginStateKey);

      checkCaseNumberRegEx(valueText);

      // Update input value
      //const { name, value } = e.target;
      const updatedFormValues = { ...formValues, [nameText]: valueText };
      setFormValues(updatedFormValues);
      const confidential = false;
      setIsConfidential(confidential)//Reset
      debouncedValidation(updatedFormValues, caseValidationType, confidential);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseChange");
    }
  };

  // Handler to show message after processing document in Right Pane
  const handleShowBatchDocMessage = (leftMessage) => {
    setLeftPaneMessage(leftMessage);
  };



  //useEffect(() => { setViewedCaseIDRecords(); });

  //=======================================================================
  // Tabs Feature - End
  //=======================================================================


  const simulateAsyncDelay = async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  //aa--------------------------------------------------------------------
  /* 
  * Authorization code starts here
  * The permission values for fields are received from Web API
  * The received data from Web API response is processed and stored in a state variable "formFieldAccess"
  */
  useEffect(() => {

    const info = sessionService.getUser();
    setUserInfo(info);
    setUserId(info ? info.legacyUserID : -123456);

    const fetchFormFieldPermissions = async () => {
      try {
        //TODO: Pass variables from session to the below URL
        const loginUserInfo = sessionService.getUser();
        const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
        const params = {
          clientID: loginStateID,
          formID: 1      // To-do: Modify Code - Hard-coded value of FormID: 1
        }
        const controlSecurityListApiData = await ApiManager.FetchControlsPermissions(params);
        const actualPermissionsForFields = DeterminePermissions(controlSecurityListApiData);
        setFormFieldAccess({ ...defaultPermissionsForFields, ...actualPermissionsForFields });
      }
      catch (error) {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchFormFieldPermissions");
      }
    };

    fetchFormFieldPermissions();

    // Soft Lock - Release locks on component unmount or route change
    const releaseLocksBeforeUnload = async () => {
      await releaseLockOnUnmount(previousLockInfoRef);
    };

    // When user navigates away, closes tab, or reloads
    window.addEventListener("beforeunload", releaseLocksBeforeUnload);
    window.addEventListener("pagehide", releaseLocksBeforeUnload);

    return () => {
      // Component unmount — same logic applies
      releaseLocksBeforeUnload();
      window.removeEventListener("beforeunload", releaseLocksBeforeUnload);
      window.removeEventListener("pagehide", releaseLocksBeforeUnload);
    };
    // End Soft Lock

  }, []);

  // Keep a ref to the latest userId to avoid stale closures in unload handlers
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Initialize asterisk display on component load
  useEffect(() => {
    const initialAsterisks = calculateRequiredAsterisks(formValues);
    setRequiredFieldLabels(initialAsterisks);
  }, [formValues.noCaseChk, formValues.docTypeSelect, formValues.noteForActionTxt, formValues.narrativeForStateTxt, formValues.caseNumberSearched]);

  //aa--------------------------------------------------------------------


  const clearFormStateValues = () => {
    try {
      setFormValues((prevValues) => ({
        ...prevValues,
        ...initialFormValues
      }));
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearFormStateValues", {});
    }
  };

  const disableExternalBatchCaseInfo = () => {
    if (caseInfoRef.current) {
      // Call the child method with parameters
      caseInfoRef.current.onDisableCaseInfoForm();
    }
  };

  const clearNewDocumentForm = () => {
    try {
      clearFormStateValues();
      noteForActionRef.current?.clear();
      narrativeForStateRef.current?.clear();
      setPrevCaseInfo(initialPrevCase);
      disableExternalBatchCaseInfo();
      setLeftPaneMessage("");
      setIsSaveAnotherTriggered(false);
      // Clear Case Array used to check for duplicates on click of "SAVE ANOTHER"
      setCaseSaveAnotherArray([]);

      const validationType = EnumUtility.CaseType.Case;
      setCaseValidationType(validationType);
      // Reset isconfidential
      const confidential = false;
      setIsConfidential(confidential)
      debouncedValidation(initialFormValues, validationType, confidential);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearNewDocumentForm", {});
    }
  };

  const handleCancelClick = async () => {
    try {
      if (requiredFormValueChanged) {
        handleConfirmationDialog(EnumUtility.ConfirmDialogType.NewDocumentCancel);
      }
      else {
        await cancelAndDisableForm();
      }

      /* if(UtilityLibrary.CompareTwoObjects(formValues, initialFormValues)) {
        await cancelAndDisableForm();
      }
      else {
        handleConfirmationDialog(EnumUtility.ConfirmDialogType.NewDocumentCancel);
      }  */
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCancelClick");
    }
  };

  const cancelAndDisableForm = async () => {
    //timerRef.current.resetTimer();
    await releaseCurrentSoftLockIfAny();
    setIsEditable(false);
    clearNewDocumentForm();
  };

  const handleCancelConfirm = async () => {
    try {
      await cancelAndDisableForm();

      // 🔍 UPDATED: Only fetch batches if they were previously loaded
      if (batchlists.length > 0) {
        await fetchBatches();
      }

      setDocGroups(docGroupsWithCase);
      loadAllDocumentTypes(documentTypeList);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCancelConfirm");
    }
  };

  const handleConfirmationDialog = (dialogContent) => {
    setConfirmationDialogContent(dialogContent);
    handleOpenConfirmDialog();
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleDialogConfirmAction = async (dialogContentID) => {
    try {
      switch (dialogContentID) {

        case EnumUtility.ConfirmDialogType.NewDocumentCancel.ID:
          handleCancelConfirm();
          break;

        case EnumUtility.ConfirmDialogType.SkipDocument.ID:
          handleSkipDocumentConfirm();
          break;

        default:
          break;
      }

      setConfirmDialogOpen(false);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDialogConfirmAction");
    }
  };

  const handleNotificationDialog = (dialogContent, timerAutoClose = false, timeToClose = 5000) => {
    setNotificationDialogContent(dialogContent);
    setTimerNotificationDialogClose(timeToClose);
    setTimerAutoCloseNotificationDialog(timerAutoClose);
    handleOpenNotificationDialog();
  };

  const handleIndexSuccessMessage = async () => {
    const loginUserInfo = sessionService.getUser();
    const params = {
      StateID: loginUserInfo?.stateID,
      MessageContentID: ClientType.MessageContent.U_UI7047_Successful_Document_Save,
    };

    const messageContentData = await ApiManager.GetMessageContentFromApi(params);
    setCustomSuccessMessage(messageContentData.messageText);
    setIndexSuccessMessageVisible(true);
  };

  // Auto-dismiss success message after configured seconds and on click
  useEffect(() => {
    const getNotificationTimer = async () => {
      if (indexSuccessMessageVisible) {
        const siteID = SessionManager.GetDataFromSession("LoginUserInfoKey")?.siteID;
        const settingsDetailIDs = [ClientType.SettingsDetail.UI_Miscellaneous__Success_Notification_TIMER];
        const results = await ApiManager.GetSettingsDetailValuesFromApi({
          settingsDetailIDList: settingsDetailIDs.join(","),
          ytracSiteID: siteID
        });

        let notificationDisplaySeconds = 3000; // default 3 seconds in milliseconds
        for (const result of results?.settingDetailsApiData || []) {
          if (result?.settingsDetailID === ClientType.SettingsDetail.UI_Miscellaneous__Success_Notification_TIMER) {
            // Convert from seconds to milliseconds
            notificationDisplaySeconds = parseInt(result?.settingsDetailValue || 3) * 1000;
          }
        }

        const timeoutId = setTimeout(() => {
          setIndexSuccessMessageVisible(false);
        }, notificationDisplaySeconds);

        const handleClick = () => {
          setIndexSuccessMessageVisible(false);
        };

        document.addEventListener('click', handleClick);

        return () => {
          clearTimeout(timeoutId);
          document.removeEventListener('click', handleClick);
        };
      }
    };

    getNotificationTimer();
  }, [indexSuccessMessageVisible]);


  useEffect(() => {
    if (!indexSuccessMessageVisible && shouldFocusCaseSearch) {
      setShouldFocusCaseSearch(false);
      setTimeout(() => {
        if (caseNumberRef.current) {
          caseNumberRef.current.focus();
        }
      }, 80);
    }
  }, [indexSuccessMessageVisible, shouldFocusCaseSearch]);

  const handleOpenNotificationDialog = () => {
    setNotificationDialogOpen(true);
  };

  const handleCloseNotificationDialog = useCallback(() => {
    setNotificationDialogOpen(false);

    // Set focus to Case Search field if a save operation just completed
    if (shouldFocusCaseSearch) {
      setShouldFocusCaseSearch(false);
      setTimeout(() => {
        if (caseNumberRef.current) {
          caseNumberRef.current.focus();
        }
      }, 100);
    }
  }, [shouldFocusCaseSearch]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    const updatedFormValues = { ...formValues, [name]: checked };
    setFormValues(updatedFormValues);
    debouncedValidation(updatedFormValues, caseValidationType);
  };

  const checkSingleDocType = async (groupID, isLoadData = false) => {
    try {
      const formattedData = await loadDocumentTypesByGroupForNoCase(groupID);
      if (formattedData && formattedData.length === 1) {
        setDocumentTypes(formattedData ? formattedData.sort((a, b) => a.name.localeCompare(b.name)) : []);
        return formattedData[0];
        //updateArrayStateValues(setPostNewDocData, { documentTypeID: formattedData[0].id });
        //updateArrayStateValues(setFormValues, { docTypeSelect: formattedData[0] });
      }
      else {
        if (isLoadData) {
          setDocumentTypes(formattedData ? formattedData.sort((a, b) => a.name.localeCompare(b.name)) : []);
        }
        else {
          setDocumentTypes(formattedData ? formattedData.sort((a, b) => a.name.localeCompare(b.name)) : []);
          //setDocumentTypes([]);
        }
        return {};
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "checkSingleDocType", {});
    }
  };

  const checkSingleDocGroup = async (docGroupList) => {
    try {
      if (docGroupList && docGroupList.length === 1) {
        setDocGroups(docGroupList);
        //updateArrayStateValues(setPostNewDocData, { docGroupID: docGroupList[0].id });
        //updateArrayStateValues(setFormValues, { docGroupSelect: docGroupList[0] });

        const selectedDocType = await checkSingleDocType(docGroupList[0].id);
        return { selectedDocGroup: docGroupList[0], selectedDocType: selectedDocType };
      }
      else {
        setDocGroups(docGroupList);
        const formattedData2 = await loadDocumentTypesByGroupForNoCase(0);
        setDocumentTypes(formattedData2 ? formattedData2.sort((a, b) => a.name.localeCompare(b.name)) : []);
        return { selectedDocGroup: {}, selectedDocType: {} };
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "checkSingleDocGroup", {});
    }
  };

  const isEmptyObject = (obj) => {
    return Object.keys(obj).length === 0;
  };

  const loadDataWhenSingleDocType = async (selectedDocGroup, selectedDocType, reason = "any") => {
    try {
      let docGroupSelectValue = { id: 0, name: "All" };
      let docTypeSelectValue = { id: -123456, name: "" };
      let narrativeText = "";

      docGroupSelectValue = (!isEmptyObject(selectedDocGroup)) ? selectedDocGroup : { id: 0, name: "All" };
      docTypeSelectValue = (!isEmptyObject(selectedDocType)) ? selectedDocType : { id: -123456, name: "" };

      if (reason !== "clear") {
        if (!isEmptyObject(selectedDocType)) {
          // Load Narrative content based on the document type selected. 
          // Condition applied when ONLY ONE DOCUMENT TYPE is AVAILABLE
          const narrativeApiData = await loadNarrativeByDocType(selectedDocGroup.id, selectedDocType, '', true);

          if (narrativeApiData && narrativeApiData.length > 0) {
            narrativeText = narrativeApiData[0];
          }

          if (narrativeForStateRef.current) {
            narrativeForStateRef.current.setValue(narrativeText);
          }
        }
      }

      return { docGroupSelectValue: docGroupSelectValue, docTypeSelectValue: docTypeSelectValue, narrativeText: narrativeText };
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "loadDataWhenSingleDocType", {});
    }
  };

  const handleNoCaseChange = async (e) => {
    try {
      const { name, checked } = e.target;
      let validationType = EnumUtility.CaseType.NoCase;
      let docGroupSelectValue = { id: 0, name: "All" };
      let docTypeSelectValue = { id: -123456, name: "" };
      let narrativeText = "";

      if (checked) {
        //timerRef.current.startTimer();
        setTimeActionStart(new Date().toISOString());

        // Load Document Group and Document Type when "NO CASE" is selected
        const { selectedDocGroup, selectedDocType } = await checkSingleDocGroup(docGroupsWithNoCase);
        const singleDocType = await loadDataWhenSingleDocType(selectedDocGroup, selectedDocType);
        docGroupSelectValue = singleDocType.docGroupSelectValue;
        docTypeSelectValue = singleDocType.docTypeSelectValue;
        narrativeText = singleDocType.narrativeText;

        formFieldsEnableLogic(dispatch, FormFieldEnableType.NoCaseSelect.ID);
        setCaseValidationType(validationType);
        narrativeForStateRef.current?.clear();
        if (caseNumberRef.current) {
          caseNumberRef.current.setValidationMessage("");
        }

        const updatedDocValues = {
          ...postNewDocData,
          nocase: 1,
          yTracCaseID: 0,
          caseNumber: "",
          docGroupID: (!isEmptyObject(selectedDocGroup)) ? selectedDocGroup.id : -1,
          documentTypeID: (!isEmptyObject(selectedDocType)) ? selectedDocType.id : 0,
          narrative: narrativeText
        };
        updateArrayStateValues(setPostNewDocData, updatedDocValues);

        clearCaseTabInfo();
      }
      else {
        //timerRef.current.resetTimer();

        // Load Document Group and Document Type when "NO CASE" is not selected
        setDocGroups(docGroupsWithCase);
        loadAllDocumentTypes(documentTypeList);

        const updatedDocValues = {
          ...postNewDocData,
          nocase: 0,
          yTracCaseID: 0,
          caseNumber: "",
          docGroupID: -1,
          documentTypeID: 0,
          narrative: narrativeText
        };
        updateArrayStateValues(setPostNewDocData, updatedDocValues);

        const configPrevCase = {
          hasPrevCase: prevCaseInfo.hasPrevCase,
          prevCaseNumber: prevCaseInfo.prevCaseNumber
        }
        formFieldsEnableLogic(dispatch, FormFieldEnableType.CaseNumberInValid.ID, configPrevCase);
        validationType = EnumUtility.CaseType.Case;
        setCaseValidationType(validationType);
      }
      const updatedFormValues = {
        ...formValues,
        [name]: checked,
        caseNumberTxt: "",
        caseNumberSearched: "",
        prevCaseChk: false,
        yTracCaseID: 0,
        docGroupSelect: docGroupSelectValue,
        docTypeSelect: docTypeSelectValue,
        narrativeForStateTxt: narrativeText
      };
      updateArrayStateValues(setFormValues, updatedFormValues);
      // Reset isConfidential
      const confidential = false;
      setIsConfidential(confidential)
      debouncedValidation(updatedFormValues, validationType);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNoCaseChange");
    }
  };

  const handlePrevCaseChange = async (e) => {
    try {
      const { name, checked } = e.target;
      if (checked) {
        const hasPrevCase = await checkPrevCaseInfo();
        if (hasPrevCase) {
          //timerRef.current.startTimer();
          setTimeActionStart(new Date().toISOString());

          const updatedFormValues = {
            ...formValues,
            [name]: checked,
            caseNumberTxt: prevCaseInfo.prevCaseNumber,
            caseNumberSearched: prevCaseInfo.prevCaseNumber
          };
          updateArrayStateValues(setFormValues, updatedFormValues);

          const loadedFromMemory = await caseInfoRef.current?.onSelectCaseFromMemory?.(
            prevCaseInfo.prevCaseNumber,
            CaseSearchSource.PreviousCaseField.ID
          );

          if (!loadedFromMemory) {
            await onCaseSearch(prevCaseInfo.prevCaseNumber, CaseSearchSource.PreviousCaseField.ID);
          }
        }
      }
      else {
        const updatedDocValues = {
          ...postNewDocData,
          yTracCaseID: 0,
          caseNumber: "",
        };
        updateArrayStateValues(setPostNewDocData, updatedDocValues);

        const updatedFormValues = {
          ...formValues,
          [name]: checked,
          caseNumberTxt: "",
          caseNumberSearched: "",
          yTracCaseID: 0,
        };
        updateArrayStateValues(setFormValues, updatedFormValues);
        debouncedValidation(updatedFormValues, EnumUtility.CaseType.Case);
        formFieldsEnableLogic(dispatch, FormFieldEnableType.PreviousCaseDeSelect.ID);
        clearCaseTabInfo();
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handlePrevCaseChange");
    }
  };

  const handleAlertClose = () => {
    setAlertProps((prevState) => ({ ...prevState, open: false }));
  };

  const createBatchLockMessage = async (modelObj) => {
    try {
      const nfnModel = new NotificationDialogModel();
      nfnModel.Title = 'Warning';
      nfnModel.Message = modelObj.lockMessage;
      nfnModel.NotificationType = EnumUtility.NotificationType.Warning;
      handleNotificationDialog(nfnModel);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "createBatchLockMessage", {});
    }
  };

  // Verify that the batch has not been soft locked or hard locked by another user during the save operation
  const checkLockBeforeSave = async (batchID) => {
    try {
      const lockData = await ApiManager.GetBatchLockStatusFromApi({
        batchID,
        masterServerDBSchema,
        softLockTime: softLockTimer
      });
      const analysis = ApiManager.AnalyzeBatchLockStatus(lockData, userIdRef.current);
      return {
        isLockedByOtherUser: analysis.isLockedByOtherUser,
        lockType: analysis.lockType,
        lockMessage: analysis.lockMessage
      };
    }
    catch (error) {
      LoggerUtility.ProcessErrorData(error, "checkLockBeforeSave", {});
      return { isLockedByOtherUser: false, lockType: 0, lockMessage: "" };
    }
  };

  const loadBatchDetails = async (batchID) => {
    try {
      clearPostNewDocData();
      setIsSaveAnotherTriggered(false);
      setPrevCaseInfo(initialPrevCase);
      SessionManager.ClearSessionValue(SessionManager.BatchDocumentsKey);
      noteForActionRef.current?.clear();
      narrativeForStateRef.current?.clear();
      const updatedFormValues = { ...formValues, ...initialFormValues, ["batchSelect"]: batchID };
      updateArrayStateValues(setFormValues, updatedFormValues);

      formFieldsEnableLogic(dispatch, FormFieldEnableType.BatchSelect.ID);
      setExtBatchID(batchID);
      /*setShowExtBatchDoc(true); */

      if (caseInfoRef.current) {
        // Call the child method with parameters
        caseInfoRef.current.onLoadDocFromBatchExternal(batchID, showExtDocGrid);
      }

      const validationType = EnumUtility.CaseType.Case;
      setCaseValidationType(validationType);
      debouncedValidation(updatedFormValues, validationType);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "loadBatchDetails", {});
    }
  };

  const clearBatchWhenLockedBatchSelected = (batchID) => {
    try {
      SessionManager.ClearSessionValue(SessionManager.BatchDocumentsKey);
      clearFormStateValues();
      noteForActionRef.current?.clear();
      narrativeForStateRef.current?.clear();
      setPrevCaseInfo(initialPrevCase);
      disableExternalBatchCaseInfo();
      setLeftPaneMessage("");
      setIsSaveAnotherTriggered(false);

      const updatedFormValues = { ...initialFormValues, batchSelect: '' };
      //const updatedFormValues = { ...initialFormValues, batchSelect: batchID };
      updateArrayStateValues(setFormValues, updatedFormValues);

      formFieldsEnableLogic(dispatch, FormFieldEnableType.BatchLockedSelect.ID);
      const validationType = EnumUtility.CaseType.Case;
      setCaseValidationType(validationType);
      debouncedValidation(updatedFormValues, validationType);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearBatchWhenLockedBatchSelected", {});
    }
  };

  const handleBatch = async (e) => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();

      const { name, value: currentBatchId } = e.target;
      const lockData = await ApiManager.GetBatchLockStatusFromApi({
        batchID: currentBatchId,
        masterServerDBSchema,
        softLockTime: softLockTimer
      });
      const lockAnalysis = ApiManager.AnalyzeBatchLockStatus(lockData, userId);

      if (lockAnalysis.isLockedByOtherUser) {
        // Batch is locked by another user
        createBatchLockMessage(lockAnalysis);
        clearBatchWhenLockedBatchSelected(currentBatchId);
      }
      else {
        // No Pre-existing Lock on this batch

        // Release soft lock on previous batch if switching batches
        await ReleaseSoftLock(currentBatchId, previousLockInfo, false);

        // Soft lock current batch
        await softLockCurrentBatch(currentBatchId, lockData);

        clearPostNewDocData();
        setIsSaveAnotherTriggered(false);
        setPrevCaseInfo(initialPrevCase);
        SessionManager.ClearSessionValue(SessionManager.BatchDocumentsKey);
        noteForActionRef.current?.clear();
        narrativeForStateRef.current?.clear();
        const updatedFormValues = { ...formValues, ...initialFormValues, [name]: currentBatchId };
        updateArrayStateValues(setFormValues, updatedFormValues);
        //setFormValues(updatedFormValues);
        //setSelectedBatch(e.target.value);

        formFieldsEnableLogic(dispatch, FormFieldEnableType.BatchSelect.ID);
        //updateArrayStateValues(setFormValues, { ["caseNumberTxt"]: "" });
        //updateFieldValues(setFormValues, { ["caseNumberTxt"]: "" });
        //setCaseNumberSearch('');
        setExtBatchID(currentBatchId);

        // Refresh batches so that the list reflects the new lock statuses for current and previously viewed batches
        const freshBatches = await fetchBatches();

        /* setShowExtBatchDoc(true); */
        //getBatchDocsByBatchID(e.target.value);

        if (caseInfoRef.current) {
          // Call the child method with parameters
          caseInfoRef.current.onLoadDocFromBatchExternal(currentBatchId, showExtDocGrid);
        }

        const validationType = EnumUtility.CaseType.Case;
        setCaseValidationType(validationType);
        debouncedValidation(updatedFormValues, validationType);
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleBatch");
    }
    finally { setIsPageLoading(false); }
  };

  const handleBatchRefreshClick = async (e) => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      await releaseCurrentSoftLockIfAny();
      //timerRef.current.resetTimer();
      clearNewDocumentForm();
      const freshBatches = await fetchBatches();

      setDocGroups(docGroupsWithCase);
      loadAllDocumentTypes(documentTypeList);

      formFieldsEnableLogic(dispatch, FormFieldEnableType.NewDocumentClick.ID);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleBatchRefreshClick");
    }
    finally { setIsPageLoading(false); }
  };

  const handleBatchDocumentProcess = async (docProcess, batchDocDetails) => {
    try {
      //const result = batchDocumentProcess.find((item) => item.ID === docProcess.ID);
      if (docProcess.ID === batchDocumentProcess.HasDocumentForIndex.ID) {
        if (batchDocDetails) {
          const updatedDocValues = {
            ...postNewDocData,
            batchID: batchDocDetails.batchID,
            batchStatusID: batchDocDetails.batchStatusID,
            batchName: batchDocDetails.batchName,
            batchDocumentID: batchDocDetails.batchDocumentID,
            documentStatusID: 5,  // TO-DO: Modify Code -- Hard-coded status value -- Completed Index Doc for Batch
            documentName: batchDocDetails.documentName,
            documentLocation: batchDocDetails.documentLocation,
            documentOrder: batchDocDetails.documentOrder,
            documentUNCPath: batchDocDetails.documentUNCPath,
            yTracSiteID: batchDocDetails.BatchSiteID,
            ["FKM_IndexUserLoginID"]: batchDocDetails.FKM_IndexUserLoginID || null,
            ["FKM_LastIndexUserLoginID"]: batchDocDetails.FKM_LastIndexUserLoginID || null,
            ["FKM_ScanUserLoginID"]: batchDocDetails.FKM_ScanUserLoginID || null
          };
          updateArrayStateValues(setPostNewDocData, updatedDocValues);
        }
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleBatchDocumentProcess");
    }
  };

  const fetchBatches = async () => {
    try {
      //TODO: Pass variables from session to the below URL
      const batchListApiData = await ApiManager.GetBatchListFromApi(softLockTimer);
      setBatchlists(batchListApiData);

      return batchListApiData; // Return fresh data immediately
    } catch (error) {
      setBatchlists([]); // Set empty array on error
      throw LoggerUtility.ProcessErrorData(error, "fetchBatches", {});
    }
  };

  // ---------------- Soft Lock ------------------

  const softLockCurrentBatch = async (currentBatchId, lockStatusResult) => {
    try {
      const softLockBatch = {
        batchID: currentBatchId,
        lockValue: 1, // apply lock
        userLoginID: userIdRef.current,
        masterServerDBSchema,
      };

      const lockResponse = await ApiManager.PostBatchLockStatusToApi(softLockBatch);

      markAsSoftLocked({
        ...lockStatusResult,
        lockedBatchId: currentBatchId,
        lockType: 2, // 2 is soft lock status
        lockUserLoginID: userIdRef.current,
      });

    } catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "trySoftLockCurrentBatch");
    }
  };

  const markAsSoftLocked = (info) => {

    // If no info provided, clear lock state
    if (!info || Object.keys(info).length === 0) {
      setpreviousLockInfo({});
      previousLockInfoRef.current = {};
      return;
    }

    // Normalize lock info
    const normalized = {
      ...info,
      lockedBatchId: info?.lockedBatchId ?? info?.batchID ?? info?.batchId ?? 0,
      lockUserLoginID: info?.lockUserLoginID ?? info?.lockUserLoginId ?? info?.lockUserId ?? userIdRef.current,
      lockType: info?.lockType ?? 2,
    };

    setpreviousLockInfo(normalized);
    previousLockInfoRef.current = normalized;
  };

  const releaseLockOnUnmount = async (previousLockInfoRef) => {
    const previousLockInfo = previousLockInfoRef?.current;
    if (!previousLockInfo || Object.keys(previousLockInfo).length === 0) return;

    const info = {
      lockedBatchId: previousLockInfo.lockedBatchId,
      lockType: previousLockInfo.lockType,
      lockUserLoginID: userIdRef.current,
    };

    await ReleaseSoftLock(
      null, // currentBatchId is not relevant on unmount
      info,
      true  // forceRelease
    );
  };

  const ReleaseSoftLock = async (
    currentBatchId,
    previousLockInfo,
    forceRelease = false
  ) => {

    // Skip if no previous lock info or it's empty (first batch)
    if (!previousLockInfo || Object.keys(previousLockInfo).length === 0) {
      return;
    }

    const previousBatchId = previousLockInfo.lockedBatchId;

    // Only release if:
    //  1. We're unmounting (leaving view), OR
    //  2. The user is switching batches (different batch IDs) OR
    //  3. The user finished all docs in a batch
    // AND the previous lock was a soft lock (lockType === 2)
    // AND the lock belongs to the current user
    if (previousLockInfo.lockType === 2 && (forceRelease || previousBatchId !== currentBatchId)) {
      try {
        const lockData = await ApiManager.GetBatchLockStatusFromApi({
          batchID: previousBatchId,
          masterServerDBSchema,
          softLockTime: softLockTimer
        });
        const lockOwnerId =
          lockData?.lockUserLoginID ??
          lockData?.lockUserLoginId ??
          -123456;

        // Get live update on batch lock, check that current lock owner is current user
        const isOwnedByCurrentUser = lockOwnerId === userIdRef.current;

        if (!isOwnedByCurrentUser) {
          return;
        }

        const releaseSoftLock = {
          batchID: previousBatchId,
          lockValue: 0, // unlock
          userLoginID: userIdRef.current,
          masterServerDBSchema,
        };

        await ApiManager.PostBatchLockStatusToApi(releaseSoftLock);
        // Clear local lock tracking after successful release
        setpreviousLockInfo({});
        previousLockInfoRef.current = {};

      } catch (error) {
        await LoggerUtility.ManageErrorLogging(
          error,
          errorFormName,
          "ReleaseSoftLock"
        );
      }
    }
  };

  const releaseCurrentSoftLockIfAny = async () => {
    let info = previousLockInfoRef?.current;

    if (!info || Object.keys(info).length === 0) {
      const currentBatchId = formValues?.batchSelect;
      if (currentBatchId) {
        info = {
          lockedBatchId: currentBatchId,
          lockType: 2,
          lockUserLoginID: userIdRef.current,
        };
      }
    }

    if (info && Object.keys(info).length > 0) {
      await ReleaseSoftLock(null, info, true);
    }

    // Clear local lock state
    markAsSoftLocked({});
  };

  // ---------------- End Soft Lock ------------------



  //dropdown loading code start.
  // useEffect(() => {
  //   try {
  //     fetchBatches();
  //   }
  //   catch (error) {
  //     LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchBatches - page load");
  //   }
  // }, []);

  //Document Group dropdown API code
  useEffect(() => {
    const fetchDocgroups = async () => {
      try {
        setIsPageLoading(true); // Show the loader
        const loginUserInfo = sessionService.getUser();
        const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
        const params = {
          clientID: loginStateID
        }
        const docGroupListApiData = await ApiManager.GetDocGroupsFromApi(params);
        if (docGroupListApiData.length > 0) {
          // Document group dropdown when "Case Number" is selected (or) "NO CASE" is not selected
          const withCaseCondition = (item) => item.caseNoCaseFlag === 2 || item.caseNoCaseFlag === 3;
          const withCaseGroupData = UtilityLibrary.filterArrayByCondition(docGroupListApiData[0], withCaseCondition);
          if (withCaseGroupData.length > 0) {
            const formattedData = withCaseGroupData.map((item) => ({
              id: item.documentGroupTypeID,
              name: item.documentGroupTypeName
            }));
            // Sort document groups alphabetically by name
            const sortedFormattedData = formattedData.sort((a, b) => a.name.localeCompare(b.name));
            setDocGroups([{ id: 0, name: "All" }, ...sortedFormattedData]);
            setDocGroupsWithCase([{ id: 0, name: "All" }, ...sortedFormattedData]); // Document group dropdown when "NO CASE" is not selected    
          }
          else {
            setDocGroups([]);
            setDocGroupsWithCase([]);
          }

          // Document group dropdown when "NO CASE" is selected
          const condition = (item) => item.caseNoCaseFlag === 1 || item.caseNoCaseFlag === 3;
          const noCaseGroupData = UtilityLibrary.filterArrayByCondition(docGroupListApiData[0], condition);
          if (noCaseGroupData.length > 0) {
            const formattedData = noCaseGroupData.map((item) => ({
              id: item.documentGroupTypeID,
              name: item.documentGroupTypeName
            }));
            // Sort document groups alphabetically by name
            const sortedFormattedData = formattedData.sort((a, b) => a.name.localeCompare(b.name));
            setDocGroupsWithNoCase([{ id: 0, name: "All" }, ...sortedFormattedData]);
            //setDocGroupsWithNoCase(formattedData);
          }
          else {
            setDocGroupsWithNoCase([]);
          }

          setDocgrouplists(docGroupListApiData[0]);

          // Store Document type list
          const allDocumentTypes = docGroupListApiData[0].flatMap(group => group.documentTypes || []);
          const formattedData = allDocumentTypes ?
            allDocumentTypes.map((item) => ({
              documentTypeID: item.documentTypeID,
              documentTypeName: item.documentTypeName,
              documentGroupTypeID: item.documentGroupTypeID,
              dT_CaseNoCaseFlag: item.dT_CaseNoCaseFlag,
              displayOrder: item.displayOrder,
            })) : [];
          setDocumentTypeList(formattedData);

          loadAllDocumentTypes(formattedData);
        }
        else {
          setDocGroups([]);
          setDocGroupsWithCase([]);
          setDocGroupsWithNoCase([]);
          setDocgrouplists([]);
          setDocumentTypes([]);
        }
      }
      catch (error) {
        setDocGroups([]);
        setDocGroupsWithCase([]);
        setDocGroupsWithNoCase([]);
        setDocgrouplists([]);
        setDocumentTypes([]);
        throw LoggerUtility.ProcessErrorData(error, "fetchDocgroups", {});
      }
      finally {
        setIsPageLoading(false); // Disable the loader
        //setLoading(false);
      }
    };


    try {
      fetchDocgroups();
    }
    catch (error) {
      LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchDocgroups - page load");
    }
  }, []);

  const loadAllDocumentTypes = async (docTypeList) => {
    try {
      const withCaseCondition2 = (item) => item.dT_CaseNoCaseFlag === 2 || item.dT_CaseNoCaseFlag === 3;
      const withCaseGroupData2 = UtilityLibrary.filterArrayByCondition(docTypeList, withCaseCondition2);
      if (withCaseGroupData2.length > 0) {
        const formattedData2 = withCaseGroupData2.map((item) => ({
          id: item.documentTypeID,
          name: item.documentTypeName
        }));
        setDocumentTypes(formattedData2 ? formattedData2.sort((a, b) => a.name.localeCompare(b.name)) : []);
      }
      else {
        setDocumentTypes([]);
      }
      /* 
      const allDocumentTypes = docGroupList.flatMap(group => group.documentTypes || []);
      const formattedData = allDocumentTypes ?
        allDocumentTypes.map((item) => ({
          id: item.documentTypeID,
          name: item.documentTypeName
        })) : [];
      setDocumentTypes(formattedData); */
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "loadAllDocumentTypes");
    }
  };

  const loadDocumentTypesByGroupForNoCase = async (groupID) => {
    try {
      if (groupID === 0) { // when "All" is selected in the document group
        const withCaseCondition2 = (item) => item.dT_CaseNoCaseFlag === 1 || item.dT_CaseNoCaseFlag === 3;
        const withCaseGroupData2 = UtilityLibrary.filterArrayByCondition(documentTypeList, withCaseCondition2);
        if (withCaseGroupData2.length > 0) {
          const formattedData2 = withCaseGroupData2.map((item) => ({
            id: item.documentTypeID,
            name: item.documentTypeName,
            displayOrder: item.displayOrder,
          }));
          return formattedData2;
        }
        else {
          return [];
        }
      }
      else {
        const selectedGroup = documentTypeList.filter(group => group.documentGroupTypeID === groupID);
        if (selectedGroup) {
          const condition = (item) => item.dT_CaseNoCaseFlag === 1 || item.dT_CaseNoCaseFlag === 3;
          const loadTypesByGroup = UtilityLibrary.filterArrayByCondition(selectedGroup, condition);
          const formattedData = loadTypesByGroup ?
            loadTypesByGroup.map((item) => ({
              id: item.documentTypeID,
              name: item.documentTypeName,
              displayOrder: item.displayOrder,
            })) : [];
          return formattedData;
        }
        else {
          return [];
        }
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "loadDocumentTypesByGroupForNoCase");
    }
  };

  //new
  const handleGroupChange = async (event, newValue, reason) => {
    try {
      let docTypeSelectValue = { id: -123456, name: "" };
      let docTypeID = 0
      let narrativeText = "";
      let groupValue = { id: -123456, name: "" };
      let groupValueId = -1;
      if (reason !== "clear") {
        groupValueId = newValue.id;
        groupValue = newValue;
      }


      const groupID = groupValueId === -1 ? -123456 : groupValueId;
      const { name, childName } = event.target;

      narrativeForStateRef.current?.clear();

      // Populate document type when "NO CASE" is not selected
      if (!formValues.noCaseChk) {
        // Populate the Document Type based on the selected Document Group
        // When Group is "0", Populate all Document Types
        // Else show the Document Types that belong to a document group
        if (groupID === 0 || groupID === null) {
          loadAllDocumentTypes(documentTypeList);
        }
        else {
          // Find the selected group and set its document types
          const selectedGroup = documentTypeList.filter(group => group.documentGroupTypeID === groupID);
          if (selectedGroup) {
            const condition = (item) => item.dT_CaseNoCaseFlag === 2 || item.dT_CaseNoCaseFlag === 3;
            const loadTypesByGroup = UtilityLibrary.filterArrayByCondition(selectedGroup, condition);
            const formattedData = loadTypesByGroup ?
              loadTypesByGroup.map((item) => ({
                id: item.documentTypeID,
                name: item.documentTypeName,
                displayOrder: item.displayOrder,
              })) : [];
            setDocumentTypes(formattedData ? formattedData.sort((a, b) => a.name.localeCompare(b.name)) : []);
          }
          else {
            setDocumentTypes([]);
          }

          /* const selectedGroup = docgrouplists.find(group => group.documentGroupTypeID === groupID);
          const formattedData = selectedGroup ?
            selectedGroup.documentTypes.map((item) => ({
              id: item.documentTypeID,
              name: item.documentTypeName
            })) : [];
          setDocumentTypes(formattedData); */
        }
      }
      // Populate document type when "NO CASE" is selected
      else {
        // Find the selected group and set its document types
        const selectedDocType = await checkSingleDocType(groupID, true);
        const singleDocType = await loadDataWhenSingleDocType(groupValue, selectedDocType, reason);
        docTypeSelectValue = singleDocType.docTypeSelectValue;
        docTypeID = (!isEmptyObject(selectedDocType)) ? selectedDocType.id : 0;
        narrativeText = singleDocType.narrativeText;
      }

      const updatedFormValues = { ...formValues, [name]: groupValue, [childName]: docTypeSelectValue, narrativeForStateTxt: narrativeText };
      setFormValues(updatedFormValues);
      updateArrayStateValues(setPostNewDocData, { docGroupID: groupValueId, documentTypeID: docTypeID, narrative: narrativeText });
      debouncedValidation(updatedFormValues, caseValidationType, isConfidential);

    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleGroupChange");
    }
  };

  const loadNarrativeByDocType = async (groupID, docType, caseNumber, isNoCase = false) => {
    // Load Narrative content based on the document type selected
    const loginUserInfo = sessionService.getUser();
    const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
    const userLogin = JSON.parse(SessionManager.GetValueFromSession(SessionManager.UserLoginDetailsKey));
    const loginUsername = userLogin ? userLogin.loginName : "";

    const narrativeObj = new NarrativeModel();
    narrativeObj.ytracSiteID = loginStateID;
    narrativeObj.batchDocumentID = postNewDocData.batchDocumentID;
    narrativeObj.docGroupID = groupID;
    narrativeObj.documentTypeID = docType.id;
    narrativeObj.documentTypeName = docType.name;
    narrativeObj.documentSourceTypeName = "";
    if (isNoCase) {
      narrativeObj.noCase = true;
    }
    else {
      narrativeObj.noCase = false;
    }
    narrativeObj.caseNumber = caseNumber;
    narrativeObj.loginName = loginUsername?.toLowerCase();
    narrativeObj.masterServerDBSchema = masterServerDBSchema;
    narrativeObj.SystemTimeZoneName = loginUserInfo?.timeZone;

    const narrativeApiData = await ApiManager.GetNarrativeFromApi(narrativeObj.toObject());
    return narrativeApiData;
  };

  const handleDocType = async (event, newValue, reason) => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();

      let typeValue = { id: -123456, name: "" };
      let typeValueId = 0;
      if (reason !== "clear") {
        typeValueId = newValue.id;
        typeValue = newValue;
      }

      const { name, parentName } = event.target;

      let groupID = formValues.docGroupSelect.id;

      if (formValues[parentName]) {
        if (formValues[parentName].id === 0) {
          groupID = getParentIdByChildId(documentTypeList, 'documentGroupTypeID', 'documentTypeID', typeValueId);
          //groupID = getParentIdByChildId(docgrouplists, 'documentGroupTypeID', 'documentTypes', 'documentTypeID', typeValueId);
          updateArrayStateValues(setPostNewDocData, { docGroupID: groupID, documentTypeID: typeValueId });
        }
        else { updateArrayStateValues(setPostNewDocData, { documentTypeID: typeValueId }); }
      }
      else { updateArrayStateValues(setPostNewDocData, { documentTypeID: typeValueId }); }

      let narrativeText = "";
      if (reason !== "clear") {
        try {
          // Load Narrative content based on the document type selected
          const narrativeApiData = await loadNarrativeByDocType(groupID, typeValue, postNewDocData.caseNumber, formValues.noCaseChk);
          if (narrativeApiData && narrativeApiData.length > 0) {
            narrativeText = narrativeApiData[0];
          }
        }
        catch (error) {
          await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDocType");
          narrativeText = "TBD";
        }

      }

      const updatedFormValues = { ...formValues, [name]: typeValue, narrativeForStateTxt: narrativeText };
      updateArrayStateValues(setFormValues, updatedFormValues);
      updateArrayStateValues(setPostNewDocData, { narrative: narrativeText });

      if (narrativeForStateRef.current) {
        narrativeForStateRef.current.setValue(narrativeText);
      }

      debouncedValidation(updatedFormValues, caseValidationType, isConfidential);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDocType");
    }
    finally { setIsPageLoading(false); }
  };

  const handleNotesChange = async (event) => {
    try {
      const { name, value } = event.target;
      const updatedFormValues = { ...formValues, [name]: value };
      setFormValues(updatedFormValues);
      updateArrayStateValues(setPostNewDocData, { notes: value });
      debouncedValidation(updatedFormValues, caseValidationType, isConfidential);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNotesChange");
    }
  };

  const handleNarrativeChange = async (event) => {
    try {
      const { name, value } = event.target;
      const updatedFormValues = { ...formValues, [name]: value };
      setFormValues(updatedFormValues);
      updateArrayStateValues(setPostNewDocData, { narrative: value });
      debouncedValidation(updatedFormValues, caseValidationType, isConfidential);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNarrativeChange");
    }
  };

  const clearBatchDocumentSession = async () => {
    try {
      // Always release any existing soft lock and reset local lock state when clearing the session
      await ReleaseSoftLock(null, previousLockInfoRef?.current, true);
      updateArrayStateValues(setLockedBatchInfo, { ...lockedBatchInfo, hasLockedBatch: false, lockedBatchID: 0 });

      SessionManager.ClearSessionValue(SessionManager.BatchDocumentsKey);
      setLeftPaneMessage("");
      clearFormStateValues();
      noteForActionRef.current?.clear();
      narrativeForStateRef.current?.clear();
      await fetchBatches();
      // Clear Case Array used to check for duplicates on click of "SAVE ANOTHER"
      setCaseSaveAnotherArray([]);
      formFieldsEnableLogic(dispatch, FormFieldEnableType.NewDocumentClick.ID);
      debouncedValidation(initialFormValues, caseValidationType, isConfidential);
      setLockedBatchInfo({ hasLockedBatch: false });
      handleNotificationDialog(EnumUtility.BatchDocumentProcess.NoPendingDocuments, true);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearBatchDocumentSession", {});
    }
  };

  const checkPrevCaseInfo = async () => {
    try {
      if (prevCaseInfo.hasPrevCase && prevCaseInfo.prevCaseNumber !== "") {
        return true;
      }
      else {
        return false;
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "checkPrevCaseInfo", {});
    }
  };

  const assignPrevCaseInfo = async (hasPrevCaseValue, prevCaseNumberValue) => {
    try {
      setPrevCaseInfo({
        hasPrevCase: hasPrevCaseValue,
        prevCaseNumber: prevCaseNumberValue
      });
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "assignPrevCaseInfo", {});
    }
  };

  const addCaseDocumentToSession = async (updatedDocValues) => {
    try {
      const caseDocObject = new CaseDocumentsModel();
      caseDocObject.scanDate = new Date().toISOString();
      caseDocObject.indexDate = updatedDocValues.indexTS;
      caseDocObject.documentGroup = formValues.docGroupSelect.name;
      caseDocObject.documentType = formValues.docTypeSelect.name;
      caseDocObject.documentSource = 'N/A';
      caseDocObject.batchDocumentID = updatedDocValues.batchDocumentID;
      caseDocObject.documentUNCPath = updatedDocValues.documentUNCPath;
      SessionManager.AddOrUpdateChildToParent(caseUniqueID, updatedDocValues.caseNumber, 'caseDocs', 'batchDocumentID', [caseDocObject.toObject()], SessionManager.CaseInformationKey);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "addCaseDocumentToSession", {});
    }
  };

  const getCaseDocumentsFromApiByCaseID = async (caseID) => {
    try {
      const params = {
        documentID: 0,
        caseNumber: caseID
      }
      const caseDocsApiData = await ApiManager.GetCaseDocsFromApiByCaseID(params);
      if (caseDocsApiData.length > 0) {
        SessionManager.AddOrUpdateChildToParent(caseUniqueID, caseID, 'caseDocs', 'batchDocumentID', caseDocsApiData, SessionManager.CaseInformationKey);
        return caseDocsApiData;
      }
      else {
        return null;
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "getCaseDocumentsFromApiByCaseID", {});
    }
  };

  const copyToClipboardFallback = (text) => {
    // Create a temporary textarea element
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // Avoid scrolling to the bottom
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0'; // Make it invisible
    document.body.appendChild(textarea);

    // Select and copy the text
    textarea.select();
    textarea.setSelectionRange(0, text.length); // Ensure all text is selected
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('Text successfully copied to clipboard');
        //alert('Text copied to clipboard!'); // Notify the user
      } else {
        throw new Error('Failed to copy text using execCommand');
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      //alert('Failed to copy text. Please try again.'); // Notify the user
    }

    // Clean up
    document.body.removeChild(textarea);
  };

  const copyToClipboard = async (textToCopy) => {
    //const textToCopy = textareaRef.current.value;

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(textToCopy)
        .then(() => {
          console.log('Text successfully copied to clipboard');
          //alert('Text copied to clipboard!'); // Optional: Notify the user
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
          // Fallback to execCommand if Clipboard API fails
          copyToClipboardFallback(textToCopy);
        });
    } else {
      // Use fallback if Clipboard API is not supported
      copyToClipboardFallback(textToCopy);
    }
  };

  const copyNarrativeToClipboard = async () => {
    try {
      if (siteSettingDetail.NarrativeCopyToClipboardIndex === EnumUtility.NarrativeCopyToClipboard.Allow) {
        const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
        await copyToClipboard(narrativeText);
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "copyNarrativeToClipboard", {});
    }
  };

  const handleSaveClick = async (event) => {
    setIsPageLoading(true);
    try {

      const isMainframeIntegration = UtilityLibrary.DetermineIfMainframeHost();
  

      if (!formValues.noCaseChk && isMainframeIntegration) {
        if (UtilityLibrary.isUsingMockData() == false) {
            const canContinue = await UtilityLibrary.checkMainframeConnectivityWithErrorHandling(
                configLibrary.apiMainframeUrl, 
                handleNotificationDialog
            );

            if (!canContinue) {
                setIsPageLoading(false);
                return; // Exit early if mainframe is not reachable
            }
        }
      }

      // Verify that the batch has not been locked (hard or soft lock) by another user
      const lockCheckResult = await checkLockBeforeSave(postNewDocData.batchID);
      if (lockCheckResult.isLockedByOtherUser) {
        setIsPageLoading(false);
        const nfnModel = new NotificationDialogModel();
        nfnModel.Title = 'Warning';
        nfnModel.Message = lockCheckResult.lockMessage;
        nfnModel.NotificationType = EnumUtility.NotificationType.Warning;
        handleNotificationDialog(nfnModel);
        return;
      }

      const loginUserInfo = sessionService.getUser();
      const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
      const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
      const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;

      await copyNarrativeToClipboard();

      await simulateAsyncDelay();
      setIsSaveAnotherTriggered(false);

      const currentDateTime = new Date().toISOString();

      // TODO: Modify Code to Pass right values to the variables below
      const updatedDocValues = {
        ...postNewDocData,
        userLoginID: loginLegacyUserID,
        fKM_UserLoginID: loginLegacyUserID,
        orgID: loginStateID,
        //yTracSiteID: loginSiteID,  // Commented: The SiteID of the Batch will be assigned to the variable
        indexTS: currentDateTime,
        lastIndexTS: currentDateTime,
        indexingCompleteTS: postNewDocData.batchStatusID === 3 ? currentDateTime : null,  // TO-DO: Modify Code -- Hard-coded status value -- Completed Index Doc for Batch
        actionStartTime: timeActionStart, // TO-DO: Modify Code - Implement timer to record the start time of an action
        masterServerDBSchema,//Added to params
        notes: noteForActionRef.current?.getValue() ?? ""

      };

      const sessionLoginUser = sessionService.getUser();
      if (sessionLoginUser.stateID === EnumUtility.StateIDKey.Mississippi.ID && (!formValues.noCaseChk)) {
        updatedDocValues.documentStatusID = 41;
      }

      updateArrayStateValues(setPostNewDocData, updatedDocValues);

      const postResponseData = await ApiManager.PostNewDocToApi(updatedDocValues);
      //const postResponseData = true;
      if (postResponseData) {

        if (sessionLoginUser.stateID === EnumUtility.StateIDKey.Mississippi.ID && (!formValues.noCaseChk)) {
          try {
            const docTransferResult = await UtilityLibrary.createAndSubmitDocTransferLog({
              formValues,
              updatedDocValues,
              postResponseData,
              loginUserInfo,
              sessionLoginUser,
              isHandleSaveAnotherClick: false
            });
          }
          catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "await UtilityLibrary.createAndSubmitDocTransferLog");
          }

        }
        // Clear Case Array used to check for duplicates on click of "SAVE ANOTHER"
        setCaseSaveAnotherArray([]);

        //await navigator.clipboard.writeText(narrativeText); // Copy Narrative to the clipboard
        //await getCaseDocumentsFromApiByCaseID(updatedDocValues.caseNumber); // No need to store case documents in session for now

        if (!formValues.noCaseChk) {
          // The Below lines of code will push Narrative of a CASE to Mainframe for configured States
          //================================================================================================================  
          const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
          // const paramsForPush = {
          //   stateID: loginStateID,
          //   apiMainframeUrl: configLibrary.apiMainframeUrl,
          //   caseNumber: formValues.caseNumberSearched,
          //   narrativeText: narrativeText,
          // };
          const responseFromPush = UtilityLibrary.writeExternalNarrative(loginStateID, false, narrativeText, {
                caseNumber: formValues.caseNumberSearched,
                onFailure: showNarrativeUpdateFailure,
                formName: null
          });
         

          //console.log(responseFromPush);
          //================================================================================================================

          // Add Newly Added/Indexed Document to the CASE in Session
          const params = {
            documentID: updatedDocValues.batchDocumentID,
            caseNumber: ""
          }
          /* const caseDocsApiData = await ApiManager.GetCaseDocsFromApiByCaseID(params);
          if (caseDocsApiData && caseDocsApiData.length > 0) {
            if (caseInfoRef.current) {
              // Call the child method with parameters
              caseInfoRef.current.addNewDocumentToGridRow(updatedDocValues.caseNumber, caseDocsApiData[0]);
            }
          } */
        }

        const updatedFormValues = {
          ...lockedBatchInfo,
          hasLockedBatch: true,
          lockedBatchID: updatedDocValues.batchID
        };
        updateArrayStateValues(setLockedBatchInfo, updatedFormValues);

        let configPrevCase = {};
        if (!formValues.noCaseChk) {
          assignPrevCaseInfo(true, updatedDocValues.caseNumber);
          configPrevCase = {
            hasPrevCase: true,
            prevCaseNumber: updatedDocValues.caseNumber,
            hasBatchLocked: true
          }
        }
        else {
          configPrevCase = {
            hasPrevCase: prevCaseInfo.hasPrevCase,
            prevCaseNumber: prevCaseInfo.prevCaseNumber,
            hasBatchLocked: true
          }
        }

        // Set flag to focus Case Search field after notification dialog closes regardless of batch status
        setShouldFocusCaseSearch(true);

        // iConnect integration is now handled automatically by the API

        if (updatedDocValues.batchStatusID !== 30) {   // TO-DO: Modify Code -- Hard-coded status value -- Completed Index Doc for Batch -- NEED TO TEST THIS. ADDED A TEMP VALUE: 30
          const batchDocs = SessionManager.GetValueFromSession(SessionManager.BatchDocumentsKey);
          if (batchDocs) {
            const batchDocList = JSON.parse(batchDocs);
            if (batchDocList.currentDocCount < batchDocList.TotalDocCount) {
              handleIndexSuccessMessage();

              clearPostNewDocData();
              clearFormFields(FormActionType.Save.ID);
              formFieldsEnableLogic(dispatch, FormFieldEnableType.SaveNewDoc.ID, configPrevCase);

              const objectManager = new ObjectUtility(batchDocList);
              objectManager.updateField(["currentIndex"], (batchDocList.currentIndex + 1));
              objectManager.updateField(["currentDocCount"], (batchDocList.currentDocCount + 1));
              const updatedBatchDoc = objectManager.getData();
              SessionManager.SetSession(SessionManager.BatchDocumentsKey, JSON.stringify(updatedBatchDoc));

              if (caseInfoRef.current) {
                // Call the child method with parameters
                caseInfoRef.current.onLoadNextDocFromBatchExternal(
                  externalBatchID,
                  showExtDocGrid,
                  updatedBatchDoc.sortedBatchDocs[updatedBatchDoc.currentIndex],
                  updatedBatchDoc.currentDocCount,
                  updatedBatchDoc.TotalDocCount);
              }
            }
            else {
              handleIndexSuccessMessage();
              await clearBatchDocumentSession();
              disableExternalBatchCaseInfo();
            }
          }
          else {
            handleIndexSuccessMessage();
            await clearBatchDocumentSession();
            disableExternalBatchCaseInfo();
          }
        }
        else {
          handleIndexSuccessMessage();
          await clearBatchDocumentSession();
          disableExternalBatchCaseInfo();
        }

        setDocGroups(docGroupsWithCase);
        loadAllDocumentTypes(documentTypeList);
      }
      //timerRef.current.resetTimer();
    }
    catch (error) {
      const errorType = error?.Details?.type;
      if ([ EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.Name,
          EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.Name].includes(errorType)) {
          let messageContentID = null;
          if (errorType === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID){
              messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System
          }
          else{
              messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
          }
          const messageContent = await SharedService.getMessageContent(messageContentID)
          handleNotificationDialog({ NotificationType: 'error', Message: messageContent?.messageText });
      }
      else {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSaveClick");
      }
    }
    finally {
      setIsPageLoading(false); // Disable the loader
    }
  };

  const checkCaseNumberOnSaveAnother = async (caseNumber) => {
    try {
      if (caseSaveAnotherArray.some(existingId => existingId.toUpperCase() === caseNumber.toUpperCase())) {
        return false;
      }
      else {
        return true;
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "checkCaseNumberOnSaveAnother", {});
    }
  };

  const onAddCaseNumberOnSaveAnother = async (caseNumber) => {
    try {
      if (!caseSaveAnotherArray.some(existingId => existingId.toUpperCase() === caseNumber.toUpperCase())) {
        setCaseSaveAnotherArray(prevIds => [...prevIds, caseNumber.toUpperCase()]);
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "onAddCaseNumberOnSaveAnother", {});
    }
  };

  const handleSaveAnotherClick = async (event) => {
    setIsPageLoading(true);
    try {

      const isMainframeIntegration = UtilityLibrary.DetermineIfMainframeHost();
  

      if (!formValues.noCaseChk && isMainframeIntegration) {
        if (UtilityLibrary.isUsingMockData() == false) {
            const canContinue = await UtilityLibrary.checkMainframeConnectivityWithErrorHandling(
                configLibrary.apiMainframeUrl, 
                handleNotificationDialog
            );
            if (!canContinue) {
                setIsPageLoading(false);
                return; // Exit early if mainframe is not reachable
            }
        }
      }

      // Verify that the batch has not been locked (hard or soft lock) by another user
      const lockCheckResult = await checkLockBeforeSave(postNewDocData.batchID);
      if (lockCheckResult.isLockedByOtherUser) {
        setIsPageLoading(false);
        const nfnModel = new NotificationDialogModel();
        nfnModel.Title = 'Warning';
        nfnModel.Message = lockCheckResult.lockMessage;
        nfnModel.NotificationType = EnumUtility.NotificationType.Warning;
        handleNotificationDialog(nfnModel);
        return;
      }

      await copyNarrativeToClipboard();

      await simulateAsyncDelay();

      const currentDateTime = new Date().toISOString();
      const loginUserInfo = sessionService.getUser();
      const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
      const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
      const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;
      // TODO: Modify Code to Pass right values to the variables below
      let updatedDocValues = {
        ...postNewDocData,
        userLoginID: loginLegacyUserID,
        fKM_UserLoginID: loginLegacyUserID,
        orgID: loginStateID,
        //yTracSiteID: loginSiteID,  // Commented: The SiteID of the Batch will be assigned to the variable
        indexTS: currentDateTime,
        lastIndexTS: currentDateTime,
        indexingCompleteTS: null,  // TO-DO: Modify Code -- Hard-coded status value -- Completed Index Doc for Batch
        actionStartTime: timeActionStart, // TO-DO: Modify Code - Implement timer to record the start time of an action
        masterServerDBSchema,  // Added to the params.
        notes: noteForActionRef.current?.getValue() ?? ""
      };

      const sessionLoginUser = sessionService.getUser();
      if (sessionLoginUser.stateID === EnumUtility.StateIDKey.Mississippi.ID && (!formValues.noCaseChk)) {
        updatedDocValues.documentStatusID = 41;
      }


      if (updatedDocValues) {
        updatedDocValues.batchStatusID = 2; // Set status to "Indexed but not completed" on Save Another
      }

      const postResponseData = await ApiManager.PostNewDocToApi(updatedDocValues);
      //const postResponseData = true;
      if (postResponseData) {

        if (sessionLoginUser.stateID === EnumUtility.StateIDKey.Mississippi.ID && (!formValues.noCaseChk)) {
          try {
            const docTransferResult = await UtilityLibrary.createAndSubmitDocTransferLog({
              formValues,
              updatedDocValues,
              postResponseData,
              loginUserInfo,
              sessionLoginUser,
              isHandleSaveAnotherClick: true
            });
          }
          catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "await UtilityLibrary.createAndSubmitDocTransferLog");
          }

        }

        setTimeActionStart(new Date().toISOString());
        //await navigator.clipboard.writeText(formValues.narrativeForStateTxt); // Copy Narrative to the clipboard
        //await getCaseDocumentsFromApiByCaseID(updatedDocValues.caseNumber); // No need to store case documents in session for now

        if (!formValues.noCaseChk) {

          // The Below lines of code will push Narrative to Mainframe for configured States
          //================================================================================================================  
          const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
          // const paramsForPush = {
          //   stateID: loginStateID,
          //   apiMainframeUrl: configLibrary.apiMainframeUrl,
          //   caseNumber: formValues.caseNumberSearched,
          //   narrativeText: narrativeText,
          // };
          const responseFromPush = await UtilityLibrary.writeExternalNarrative(loginStateID, false, narrativeText, {
                caseNumber: formValues.caseNumberSearched,
                onFailure: showNarrativeUpdateFailure,
                formName: null
          });
          
          //================================================================================================================

          // Add Case Number to array on click of SAVE ANOTHER
          // The array will be used to avaoid assigning the CASE NUMBER more than once to the same document
          await onAddCaseNumberOnSaveAnother(formValues.caseNumberSearched);

          // Add Newly Added/Indexed Document to the CASE in Session
          const params = {
            documentID: updatedDocValues.batchDocumentID,
            caseNumber: ""
          }
          /* const caseDocsApiData = await ApiManager.GetCaseDocsFromApiByCaseID(params);
          if (caseDocsApiData && caseDocsApiData.length > 0) {
            if (caseInfoRef.current) {
              // Call the child method with parameters
              caseInfoRef.current.addNewDocumentToGridRow(updatedDocValues.caseNumber, caseDocsApiData[0]);
            }
          } */
        }

        const updatedFormValues = {
          ...lockedBatchInfo,
          hasLockedBatch: true,
          lockedBatchID: updatedDocValues.batchID
        };
        updateArrayStateValues(setLockedBatchInfo, updatedFormValues);
        let configPrevCase = {};
        if (!formValues.noCaseChk) {
          assignPrevCaseInfo(true, updatedDocValues.caseNumber);
          configPrevCase = {
            hasPrevCase: true,
            prevCaseNumber: updatedDocValues.caseNumber,
            hasBatchLocked: true
          }
        }
        else {
          configPrevCase = {
            hasPrevCase: prevCaseInfo.hasPrevCase,
            prevCaseNumber: prevCaseInfo.prevCaseNumber,
            hasBatchLocked: true
          }
        }

        // iConnect integration is now handled automatically by the API

        setIsSaveAnotherTriggered(true);

        updatedDocValues = {
          ...updatedDocValues,
          yTracCaseID: 0,
          caseNumber: "",
          nocase: 0,
        };
        updateArrayStateValues(setPostNewDocData, updatedDocValues);

        clearCaseTabInfo();


        if (caseInfoRef.current && caseInfoRef.current.clearAllCaseUIState) {
          await caseInfoRef.current.clearAllCaseUIState();
        }

        handleIndexSuccessMessage();

        // Set flag to focus Case Search field after notification dialog closes
        setShouldFocusCaseSearch(true);

        clearFormFields(FormActionType.SaveAnother.ID);
        formFieldsEnableLogic(dispatch, FormFieldEnableType.SaveAnother.ID, configPrevCase);

        setDocGroups(docGroupsWithCase);
        loadAllDocumentTypes(documentTypeList);
      }
      //timerRef.current.resetTimer();
    }
    catch (error) {
      const errorType = error?.Details?.type;
      if ([ EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.Name,
          EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.Name].includes(errorType)) {
          let messageContentID = null;
          if (errorType === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID){
              messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System
          }
          else{
              messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
          }
          const messageContent = await SharedService.getMessageContent(messageContentID)
          handleNotificationDialog({ NotificationType: 'error', Message: messageContent?.messageText });
      }
      else {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSaveAnotherClick");
      }
    }
    finally {
      setIsPageLoading(false); // Disable the loader
    }
  };

  const handleSkipDocumentClick = async () => {
    try {
      handleConfirmationDialog(EnumUtility.ConfirmDialogType.SkipDocument);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSkipDocumentClick");
    }
  };

  const showNarrativeUpdateFailure = (apiErrorMessage) => {
      handleNotificationDialog({
        NotificationType: EnumUtility.NotificationType.Error,
        Title: 'Error',
        Message: apiErrorMessage || 'Failed to update narrative. Check the scraper app and the mainframe.'
      });
  };

  const handleSkipDocumentConfirm = async (event) => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      setIsSaveAnotherTriggered(false);

      const updatedDocValues = postNewDocData;
      let configPrevCase = {};
      if (!formValues.noCaseChk) {
        //assignPrevCaseInfo(true, updatedDocValues.caseNumber);
        configPrevCase = {
          hasPrevCase: prevCaseInfo.hasPrevCase,
          prevCaseNumber: prevCaseInfo.prevCaseNumber,
          hasBatchLocked: true
        }
      }

      // Set flag to focus Case Search field after skipping document
      setShouldFocusCaseSearch(true);

      const batchDocs = SessionManager.GetValueFromSession(SessionManager.BatchDocumentsKey);
      if (batchDocs) {
        const batchDocList = JSON.parse(batchDocs);
        if (batchDocList.currentDocCount < batchDocList.TotalDocCount) {
          clearPostNewDocData();
          clearFormFields(FormActionType.Save.ID);
          formFieldsEnableLogic(dispatch, FormFieldEnableType.SaveNewDoc.ID, configPrevCase);

          const objectManager = new ObjectUtility(batchDocList);
          objectManager.updateField(["currentIndex"], (batchDocList.currentIndex + 1));
          objectManager.updateField(["currentDocCount"], (batchDocList.currentDocCount + 1));
          const updatedBatchDoc = objectManager.getData();
          SessionManager.SetSession(SessionManager.BatchDocumentsKey, JSON.stringify(updatedBatchDoc));

          if (caseInfoRef.current) {
            // Call the child method with parameters
            caseInfoRef.current.onLoadNextDocFromBatchExternal(
              externalBatchID,
              showExtDocGrid,
              updatedBatchDoc.sortedBatchDocs[updatedBatchDoc.currentIndex],
              updatedBatchDoc.currentDocCount,
              updatedBatchDoc.TotalDocCount);
          }
        }
        else {
          await clearBatchDocumentSession();
          disableExternalBatchCaseInfo();
        }
      }
      else {
        await clearBatchDocumentSession();
        disableExternalBatchCaseInfo();
      }

      setDocGroups(docGroupsWithCase);
      loadAllDocumentTypes(documentTypeList);
      //timerRef.current.resetTimer();
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSkipDocumentConfirm");
    }
    finally {
      setIsPageLoading(false); // Disable the loader
    }
  };



  const allowedRoleNumbers = [0, 1, 2, 3, 10];
  const loginUserInfo = sessionService.getUser();
  const showReindex = loginUserInfo && allowedRoleNumbers.includes(loginUserInfo.roleID);

  const leftPaneContent = (
    <div className="content-main-container">
      {!isEditable && (
        <div className="banner-blue">
          <div className="button-group" style={{ display: 'flex', justifyContent: showReindex ? 'space-between' : 'center', gap: '1rem' }}>
            <div className="base-wrapper">
              {!(formFieldAccess.newDocumentBtn?.hide ?? false) && (
                <div className="base-2">
                  <img className="masked-icon" alt="Masked icon" src='/doc-New.png' />
                  <Button id="newDocumentBtn" name="newDocumentBtn" className="button-2" onClick={handleNewDocumentClick} disabled={!(formFieldAccess.newDocumentBtn?.write ?? true)}>INDEX</Button>
                </div>
              )}
            </div>
            {showReindex && (
              <div className="base-wrapper">
                {!(formFieldAccess.newDocumentBtn?.hide ?? false) && (
                  <div className="base-2">
                    <img className="masked-icon" alt="Masked icon" src='/doc-New.png' />
                    <Button id="newDocumentBtn" name="newDocumentBtn" className="button-2" onClick={handleReIndexClick} disabled={!(formFieldAccess.newDocumentBtn?.write ?? true)}>RE-INDEX</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="pane-wrap-container">
        <div className={`${isEditable ? 'pane-main-container' : 'pane-main-container-2 greyed-out'}`}>
          <div className={`${isPageLoading ? ' greyed-out' : isEditable ? 'form-section editable' : 'form-section-2 '}`}>
            <div className="text-wrapper-header">Index</div>
            <div className={`${isEditable ? 'form-main-container' : 'form-main-container-2'}`}>
              <div className="form-top-content">
                <div className="row p-3">

                  {!(formFieldAccess.batchSelect?.hide ?? false) && (
                    <div className="col-12 mb-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FormControl fullWidth>
                          <InputLabel id="batchSelect-label" shrink={true}>Batch</InputLabel>
                          <Select
                            labelId="batchSelect-label"
                            id="batchSelect"
                            name="batchSelect"
                            value={formValues.batchSelect}
                            iconcomponent={ArrowDropDown}
                            label="Batch"
                            displayEmpty={true}
                            disabled={!formState.batchSelect || lockedBatchInfo.hasLockedBatch}
                            onChange={handleBatch}
                          >
                            <MenuItem value="" disabled>-- Please select a batch --</MenuItem>
                            {batchlists.map((batchlist) => (
                              <MenuItem key={batchlist.batchID} value={batchlist.batchID}>
                                {batchlist.batchName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <label>
                          <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.batchSelect}</span>
                        </label>
                        {/* Refresh Icon Button */}
                        <Tooltip title="Reload Batch" arrow disableHoverListener={!formState.ibRefreshBatch} disableFocusListener={!formState.ibRefreshBatch}>
                          <span>
                            <IconButton
                              name="ibRefreshBatch"
                              aria-label="refresh"
                              sx={{
                                color: '#5A73EA', // Custom color (greenish)
                                '&:hover': {
                                  color: '#fff', // Darker green on hover
                                  backgroundColor: '#5A73EA', // Subtle background on hover
                                },
                                fontSize: '24px', // Icon size
                                padding: '8px', // Icon padding
                              }}
                              onClick={handleBatchRefreshClick}
                              disabled={!formState.ibRefreshBatch}
                            >
                              <RefreshIcon fontSize="large" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                    </div>

                  )}

                  <div className={`col-12 col-md-${'12'} mb-3 d-flex align-items-center justify-center`}>
                    {!(formFieldAccess.caseNumberTxt?.hide ?? false) && (
                      <div className={`col-6 col-md-${'6'}`} style={{ marginRight: "5px" }}>
                        <TextFieldWithIconButton
                          ref={caseNumberRef}
                          nameForField="caseNumberTxt"
                          onIconButtonClick={handleCaseSearch}
                          onClearButtonClick={handleCaseClear}
                          onTextChange={handleCaseChange}
                          valueForField={formValues.caseNumberTxt}
                          labelForField="Case Search"
                          iconsTooltipText={{ search: 'Search Case', copy: 'Copy Case', clear: 'Clear Case' }}
                          iconsDisabled={caseNumberIconsDisabled}
                          isDisabled={!formState.caseNumberTxt}
                          requiredFieldLabel={requiredFieldLabels.caseNumberTxt}
                          disableKeyDown={disableKeyDownOnCaseSearch}
                        />
                      </div>
                    )}

                    <div className={`col-6 col-md-${'6'} d-flex justify-content-evenly`}>
                      {!(formFieldAccess.noCaseChk?.hide ?? false) && (
                        <div>
                          <FormControlLabel
                            name="noCaseChk"
                            control={<CustomCheckbox />}
                            label="No Case"
                            checked={formValues.noCaseChk}
                            onChange={handleNoCaseChange}
                            disabled={!formState.noCaseChk}
                          />
                          <label>
                            <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.noCaseChk}</span>
                          </label>
                        </div>
                      )}
                      {!(formFieldAccess.prevCaseChk?.hide ?? false) && (
                        <div>
                          <FormControlLabel
                            name="prevCaseChk"
                            control={<CustomCheckbox />}
                            label="Previous Case"
                            checked={formValues.prevCaseChk}
                            onChange={handlePrevCaseChange}
                            disabled={!formState.prevCaseChk} />
                          <label>
                            <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.prevCaseChk}</span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  {!(formFieldAccess.docGroupSelect?.hide ?? false) && (
                    <div className="col-12 mb-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FormControl fullWidth>
                          <Autocomplete
                            options={docGroups}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}

                            id="docGroupSelect"
                            value={docGroups.find((opt) => opt.id === formValues.docGroupSelect.id) || null}
                            //value={docGroups.find((opt) => opt.id === formValues.docGroupSelect) || null}
                            iconcomponent={ArrowDropDown}
                            label="Document Group"
                            noOptionsText="Document Group not found"
                            disabled={!formState.docGroupSelect}

                            onChange={(event, newValue, reason) => {
                              // Manually construct the target object for consistency
                              const modifiedEvent = {
                                target: {
                                  name: "docGroupSelect", // Define the name of the Autocomplete
                                  childName: "docTypeSelect" // Define the name of the child -- Document Type
                                },
                              };
                              handleGroupChange(modifiedEvent, newValue, reason);
                            }}
                            disablePortal

                            renderInput={(params) => (
                              <TextField {...params}
                                label="Document Group"
                                variant="outlined"
                                InputLabelProps={{
                                  shrink: true, // Always keeps the label shrunk
                                }}
                              />)}
                          >
                          </Autocomplete>
                        </FormControl>
                        <label>
                          <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.docGroupSelect}</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {!(formFieldAccess.docTypeSelect?.hide ?? false) && (
                    <div className="col-12 mb-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FormControl fullWidth>
                          <Autocomplete
                            options={documentTypes}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}

                            id="docTypeSelect"
                            value={documentTypes.find((opt) => opt.id === formValues.docTypeSelect.id) || null}
                            //value={documentTypes.find((opt) => opt.id === formValues.docTypeSelect) || null}
                            iconcomponent={ArrowDropDown}
                            label="Document Type"
                            noOptionsText="Document Type not found"
                            disabled={!formState.docTypeSelect}

                            onChange={(event, newValue, reason) => {
                              // Manually construct the target object for consistency
                              const modifiedEvent = {
                                target: {
                                  name: "docTypeSelect", // Define the name of the Autocomplete
                                  parentName: "docGroupSelect" // Define the name of the parent -- Document Group
                                },
                              };
                              handleDocType(modifiedEvent, newValue, reason);
                            }}
                            disablePortal

                            renderInput={(params) => (
                              <TextField {...params}
                                label="Document Type"
                                variant="outlined"
                                InputLabelProps={{
                                  shrink: true, // Always keeps the label shrunk
                                }}
                              />)}
                          >
                          </Autocomplete>
                        </FormControl>
                        <label>
                          <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.docTypeSelect}</span>
                        </label>
                      </div>
                    </div>
                  )}
                  {!(formFieldAccess.noteForActionTxt?.hide ?? false) && (
                    <div className="col-12 mb-3 align-items-center justify-center">
                      <TextAreaWithCount
                        ref={noteForActionRef}
                        id="noteForActionTxt"
                        nameForField="noteForActionTxt"
                        labelValue="Y-Trac Note for Action Taken"
                        maxLength={notesCharLimit}
                        placeholder=""
                        isDisabled={!formState.noteForActionTxt}
                        onTextChange={handleNotesChange}
                        requiredFieldLabel={requiredFieldLabels.noteForActionTxt}
                      />
                    </div>
                  )}
                  {!(formFieldAccess.narrativeForStateTxt?.hide ?? false) && (
                    <div className="col-12 mb-3 align-items-center justify-center">
                      <TextAreaWithCount
                        ref={narrativeForStateRef}
                        id="narrativeForStateTxt"
                        nameForField="narrativeForStateTxt"
                        labelValue="Narrative for State System"
                        maxLength={narrativeCharLimit}
                        placeholder=""
                        isDisabled={!formState.narrativeForStateTxt}
                        onTextChange={handleNarrativeChange}
                        requiredFieldLabel={requiredFieldLabels.narrativeForStateTxt}
                      />
                    </div>
                  )}

                </div>
                {/* Index Success Message */}
                {indexSuccessMessageVisible && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      backgroundColor: '#2E7D32',
                      borderRadius: '8px',
                      padding: '12px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '500',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }}>
                      <CheckCircleIcon style={{
                        fontSize: '20px',
                        color: 'white'
                      }} />
                      {customSuccessMessage}
                    </div>
                  </div>
                )}
                <div className='d-flex justify-content-between align-items-center mb-2  form-actions'>
                  {!(formFieldAccess.cancelBtn?.hide ?? false) && (
                    <div>
                      <Button id="cancelBtn" name="cancelBtn" color="ywButtonWhite" variant='outlined' onClick={handleCancelClick}>Cancel</Button>
                    </div>
                  )}
                  {!(formFieldAccess.leftPaneMessageLbl?.hide ?? false) && (
                    <div className="center-section text-wrapper-message">
                      <div><label id="leftPaneMessageLbl" name="leftPaneMessageLbl">{leftPaneMessage}</label></div>
                      {isTimerVisible &&
                        <div><label className='timer-message'>Timer: {timerView}</label></div>
                      }
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "18px" }}>
                    {!(formFieldAccess.saveBtn?.hide ?? false) && (
                      <Button id="saveBtn" name="saveBtn" color="ywButtonBlack" variant='contained' className='me-2' disabled={!isSaveEnabled} onClick={handleSaveClick}>Save</Button>
                    )}
                    {!(formFieldAccess.saveAnotherBtn?.hide ?? false) && (
                      <Button id="saveAnotherBtn" name="saveAnotherBtn" color="ywButtonBlue" variant='contained' className='me-2' disabled={!isSaveAnotherEnabled} onClick={handleSaveAnotherClick}>Save + Another</Button>
                    )}
                    {!(formFieldAccess.skipDocumentBtn?.hide ?? false) && formState.skipDocumentBtn && (
                      <Button id="skipDocumentBtn" name="skipDocumentBtn" color="ywButtonBlue" variant='contained' onClick={handleSkipDocumentClick}>Skip Document</Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
    </div>
  );

  const rightPaneContent = (
    <div style={{ width: '100%', height: '100%' }}>
      <CaseInformationPane
        ref={caseInfoRef}
        onCaseSearchComplete={handleCaseSearchComplete}
        onBatchDocAvailable={handleBatchDocumentProcess}
        onMessageUpdate={handleShowBatchDocMessage}
      />
    </div>
  );

  return (
    <div>
      <ToggleSplitPane
        leftPaneVisibility={true}
        leftPaneTitle={leftPaneTitle}
        leftPaneContent={leftPaneContent}
        rightPaneTitle={rightPaneTitle}
        rightPaneContent={rightPaneContent}
        leftPaneSize='50%'
      >
      </ToggleSplitPane>

      <AlertNotification
        alertType={alertProps.type}
        message={alertProps.message}
        open={alertProps.open}
        onClose={handleAlertClose}
      />

      {/* Include the loader component */}
      <PageLoader isPageLoading={isPageLoading} />

      {/* Include the Confirmation Dialog component */}
      <ConfirmationDialog
        openConfirmation={confirmDialogOpen}
        dialogContent={confirmationDialogContent}
        onCloseConfirmation={handleCloseConfirmDialog}
        onConfirmDialog={(param) => handleDialogConfirmAction(param)}
      />

      {/* Include the Notification Dialog component */}
      <NotificationDialog
        openNotification={notificationDialogOpen}
        dialogContent={notificationDialogContent}
        onCloseNotification={handleCloseNotificationDialog}
        isAutoClose={timerAutoCloseNotificationDialog}
        timerDuration={timerNotificationDialogClose}
      />

    </div>
  );
});

export default NewDocument;
